/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUp, Plus, Check, Copy, ExternalLink, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import LanguageToggle from "../components/LanguageToggle";
import { useSurveyCircle } from "../hooks/useSurveyCircle";
import { useTrafficSource } from "../hooks/useTrafficSource";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PROLIFIC_COMPLETION_CODE = process.env.REACT_APP_PROLIFIC_COMPLETION_CODE || "";
const PROLIFIC_REDIRECT_BASE = process.env.REACT_APP_PROLIFIC_REDIRECT_BASE || "https://app.prolific.com/submissions/complete";
const PROLIFIC_REDIRECT_DELAY_MS = 5000;

/**
 * v5.1 — Detects short answers that describe a body location or physical
 * sensation, or non-answers ("I am not tense"). Used ONLY on step 3 (trade-off)
 * to trigger a single clarification round before storing the answer as a
 * conceptual trade-off.
 *
 * Heuristic (intentionally cautious — false positives just ask one extra Q):
 *   - trim + lowercase, strip punctuation
 *   - if ≤ 6 words AND contains no conjunction (and/or/vs/versus/en/of/y/o)
 *     AND matches at least one body-word or non-answer pattern → true
 */
const BODY_WORDS = new Set([
  // English
  "shoulder", "shoulders", "chest", "head", "neck", "back", "stomach", "belly",
  "jaw", "throat", "arm", "arms", "leg", "legs", "foot", "feet", "hand", "hands",
  "muscle", "muscles", "gut", "heart", "temple", "temples", "forehead", "spine",
  // Dutch
  "schouder", "schouders", "borst", "hoofd", "nek", "rug", "buik", "kaak", "keel",
  "arm", "armen", "been", "benen", "voet", "voeten", "hand", "handen", "spier",
  "spieren", "hart", "slaap", "slapen", "voorhoofd",
  // Spanish
  "hombro", "hombros", "pecho", "cabeza", "cuello", "espalda", "estómago", "estomago",
  "barriga", "mandíbula", "mandibula", "garganta", "brazo", "brazos", "pierna",
  "piernas", "pie", "pies", "mano", "manos", "músculo", "musculo", "músculos",
  "musculos", "corazón", "corazon", "sien", "sienes", "frente",
]);

const NON_ANSWER_PATTERNS = [
  /^i\s*(?:am|['`’]m)\s*not\s*tense\b/,
  /^no\s+tension\b/,
  /^(?:none|nothing|nowhere|no)\.?$/,
  /^i\s*don['`’]t\s*(?:feel|know)/,
  /^geen\s+spanning\b/,
  /^ik\s+ben\s+niet\s+gespannen\b/,
  /^(?:niks|niets|nergens|nee)\.?$/,
  /^ik\s+weet\s+het\s+niet\b/,
  /^no\s+estoy\s+tens[oa]\b/,
  /^sin\s+tensi[oó]n\b/,
  /^(?:nada|ninguna|ninguno|no)\.?$/,
  /^no\s+lo\s+s[eé]\b/,
];

const CONJUNCTIONS = new Set([
  "and", "or", "vs", "vs.", "versus",
  "en", "of",
  "y", "e", "o", "u",
]);

const isBodyOrNonAnswer = (raw) => {
  if (!raw) return false;
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/[.,;:!?"'`’()\[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return false;

  // Non-answer patterns first (they're unambiguous)
  if (NON_ANSWER_PATTERNS.some((rx) => rx.test(cleaned))) return true;

  const words = cleaned.split(" ");
  if (words.length > 6) return false;
  if (words.some((w) => CONJUNCTIONS.has(w))) return false;

  return words.some((w) => BODY_WORDS.has(w));
};

/**
 * v5.1 Sprint 2 — Conversation-Fit Detection.
 * Recognises clear feedback ABOUT the reflection conversation itself
 * (not about the user's life or emotional state). Conservative by design:
 * when in doubt, we do NOT flag and the normal reflection continues.
 *
 * Explicitly does NOT match:
 *   - "I feel overwhelmed"       (emotional state)
 *   - "I don't know what to choose" (situational uncertainty)
 *   - "I'm frustrated because..." (life frustration)
 */
const CONV_FIT_PATTERNS = [
  // ── English ────────────────────────────────────────────
  /\bpointless\b/i,
  /\b(this|it|these\s+questions?)\s+(is(n'?t| not)|are(n'?t| not))\s+help(ing|ful)/i,
  /\b(this|it|these\s+questions?|the\s+(conversation|questions))\s+(doesn'?t|does\s+not|don'?t|do\s+not)\s+(fit|help|apply|make\s+sense)/i,
  /\byou\s+(don'?t|do\s+not)\s+understand(\s+me)?\b/i,
  /\b(these?\s+questions?|the\s+questions?)\s+(are|is|feel|seem)\s+(irrelevant|not\s+relevant)/i,
  /\bnothing\s+is\s+(becoming|getting)\s+(clearer|clear)\b/i,
  /\b(i\s+want\s+to\s+stop|i\s+wanna\s+stop|let\s+me\s+stop|i'?d\s+like\s+to\s+stop|i'?d\s+rather\s+stop)\b/i,
  /\b(this\s+conversation|our\s+conversation|the\s+conversation)\s+(is\s+going\s+nowhere|goes\s+nowhere|isn'?t\s+helping|doesn'?t\s+help)\b/i,
  /\bthis\s+(is\s+not|isn'?t)\s+working\b/i,
  /^i\s+(don'?t|do\s+not)\s+understand\s*(this|these|it|any\s+of\s+this|(the|these)\s+questions?)?\.?$/i,
  /\bi\s+(don'?t|do\s+not)\s+understand\s+(these|the)\s+questions?\b/i,
  /^(i\s+don'?t\s+get\s+(it|this)|this\s+makes\s+no\s+sense)\.?$/i,
  /\bthe\s+questions?\s+don'?t\s+(really\s+)?fit\b/i,

  // ── Dutch ──────────────────────────────────────────────
  /\bzinloos\b/i,
  /\b(dit|het|deze\s+vragen)\s+helpt?\s+(mij\s+)?niet\b/i,
  /\b(dit|het|deze\s+vragen)\s+past?\s+niet\b/i,
  /\bje\s+begrijpt?\s+(mij|me)\s+niet\b/i,
  /\bdeze\s+vragen\s+(passen|helpen)\s+niet\b/i,
  /\b(ik\s+wil\s+stoppen|ik\s+wil\s+ermee\s+stoppen|laat\s+me\s+stoppen)\b/i,
  /\bhet\s+wordt\s+niet\s+(duidelijker|helderder)\b/i,
  /\b(dit\s+gesprek|deze\s+conversatie)\s+(gaat\s+nergens\s+heen|leidt\s+nergens\s+toe|helpt\s+niet)\b/i,
  /^ik\s+snap\s+(dit|het|deze\s+vragen)?\s*niet\.?$/i,
  /\bik\s+snap\s+deze\s+vragen\s+niet\b/i,

  // ── Spanish ────────────────────────────────────────────
  /\b(esto|esta\s+conversación|estas\s+preguntas?)\s+no\s+tiene\s+sentido\b/i,
  /\b(esto|esta\s+conversación|estas\s+preguntas?)\s+no\s+(está\s+)?ayudando\b/i,
  /\b(esto|esta\s+conversación|estas\s+preguntas?)\s+no\s+(me\s+)?ayuda\b/i,
  /\b(esto|esta\s+conversación|estas\s+preguntas?)\s+no\s+encaja(n)?\b/i,
  /\bno\s+me\s+entiendes\b/i,
  /\b(estas\s+preguntas?)\s+(son|es)\s+irrelevantes?\b/i,
  /\b(quiero\s+parar|quiero\s+detenerme|quiero\s+terminar)\b/i,
  /\bno\s+(se\s+está\s+aclarando|(está\s+)?quedando\s+más\s+claro)\b/i,
  /\b(esta\s+conversación)\s+no\s+lleva\s+a\s+ninguna\s+parte\b/i,
  /^no\s+(entiendo|comprendo)\s*(esto|nada\s+de\s+esto|estas\s+preguntas)?\.?$/i,
  /\bno\s+(entiendo|comprendo)\s+(estas|las)\s+preguntas?\b/i,
];

const detectConversationFit = (raw) => {
  if (!raw) return false;
  const text = raw.trim();
  if (!text) return false;
  return CONV_FIT_PATTERNS.some((rx) => rx.test(text));
};

// Detect headless/automated browsers and use shorter delays for them
const IS_AUTOMATED =
  typeof navigator !== "undefined" &&
  (navigator.webdriver === true ||
    /HeadlessChrome|PhantomJS|playwright/i.test(navigator.userAgent || ""));

const TYPING_DELAY = IS_AUTOMATED ? 80 : 600;
const BREATH_DELAY = IS_AUTOMATED ? 60 : 400;
const TOTAL_STEPS = 5;

/* ───────── Sidebar ───────── */
const Sidebar = ({ t, sessions, currentId, onNewSession, onSelectSession }) => (
  <aside
    data-testid="sidebar"
    className="hidden md:flex flex-col"
    style={{
      width: "240px",
      borderRight: "1px solid var(--border-soft)",
      background: "var(--bg-warm)",
      padding: "1.2rem 0.9rem",
    }}
  >
    <div className="flex items-center gap-2.5 px-1 mb-7">
      <span className="sage-dot" />
      <span
        className="font-sans"
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--ink-mid)",
          fontWeight: 500,
        }}
      >
        {t.brandName}
      </span>
    </div>

    <button
      data-testid="new-session-btn"
      className="new-sess-btn"
      onClick={onNewSession}
    >
      <Plus className="w-3.5 h-3.5" />
      <span>{t.newSession || "New session"}</span>
    </button>

    <div className="mt-6 mb-2 px-1">
      <span
        className="font-sans"
        style={{
          fontSize: "0.66rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: "var(--ink-soft)",
          fontWeight: 500,
        }}
      >
        {t.recent || "Recent"}
      </span>
    </div>

    <div className="flex-1 overflow-y-auto space-y-0.5">
      {sessions.length === 0 && (
        <p
          className="px-2 font-serif italic"
          style={{ fontSize: "0.78rem", color: "var(--ink-ghost)" }}
          data-testid="no-sessions"
        >
          {t.noSessions || "No reflections yet."}
        </p>
      )}
      {sessions.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelectSession(s)}
          className={`sess-item w-full text-left ${
            s.id === currentId ? "current" : ""
          }`}
          data-testid={`session-${s.id}`}
        >
          <div
            className="font-sans truncate"
            style={{
              fontSize: "0.82rem",
              color: "var(--ink)",
              fontWeight: 400,
            }}
          >
            {s.title || t.untitledSession || "Untitled"}
          </div>
          <div
            className="font-sans"
            style={{
              fontSize: "0.66rem",
              color: "var(--ink-ghost)",
              marginTop: "0.15rem",
            }}
          >
            {new Date(s.created_at).toLocaleDateString()}
          </div>
        </button>
      ))}
    </div>

    <div
      className="mt-4 pt-4 flex items-center justify-between"
      style={{ borderTop: "1px solid var(--border-soft)" }}
    >
      <LanguageToggle />
    </div>
  </aside>
);

/* ───────── AI message ───────── */
const AiMessage = ({ children, italic = false, helper, testId }) => (
  <div className="flex items-start gap-4 animate-enter" data-testid={testId}>
    <div
      style={{
        width: "1.5rem",
        height: "1.5rem",
        flexShrink: 0,
        marginTop: "0.35rem",
        borderRadius: "50%",
        border: "1px solid var(--sage-border)",
        background: "var(--sage-soft)",
        position: "relative",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "5px",
          height: "5px",
          borderRadius: "50%",
          background: "var(--sage)",
          opacity: 0.7,
        }}
      />
    </div>
    <div className="flex-1 min-w-0">
      <p className={`ai-text ${italic ? "question" : ""}`}>{children}</p>
      {helper && (
        <p
          data-testid="ai-helper"
          style={{
            marginTop: "0.65rem",
            fontFamily: "'DM Mono', ui-monospace, monospace",
            fontSize: "0.86rem",
            color: "var(--ink-3)",
            lineHeight: 1.65,
            whiteSpace: "pre-line",
          }}
        >
          {helper}
        </p>
      )}
    </div>
  </div>
);

/* ───────── User message ───────── */
const UserMessage = ({ children }) => (
  <div className="flex justify-end animate-enter" data-testid="user-message">
    <div className="user-msg">{children}</div>
  </div>
);

/* ───────── Typing indicator + breath ───────── */
const TypingTurn = () => (
  <div className="flex items-start gap-4 animate-fade-in">
    <div
      style={{
        width: "1.5rem",
        height: "1.5rem",
        flexShrink: 0,
        marginTop: "0.35rem",
        borderRadius: "50%",
        border: "1px solid var(--sage-border)",
        background: "var(--sage-soft)",
      }}
    />
    <div className="flex items-center gap-1.5 pt-2">
      <span
        className="typing-dot"
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "var(--ink-soft)",
          display: "inline-block",
        }}
      />
      <span
        className="typing-dot"
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "var(--ink-soft)",
          display: "inline-block",
        }}
      />
      <span
        className="typing-dot"
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "var(--ink-soft)",
          display: "inline-block",
        }}
      />
    </div>
  </div>
);

const BreathTurn = () => (
  <div className="flex justify-center py-1 animate-fade-in">
    <div className="breath-line" />
  </div>
);

/* ───────── Values picker ───────── */
const ValuesPicker = ({
  t,
  selectedValues,
  valueReasons,
  onToggle,
  onUpdateReason,
  customValue,
  onCustomValueChange,
  onAddCustom,
  onContinue,
  onSkip,
  canContinue,
}) => (
  <div
    className="animate-enter"
    style={{
      padding: "1.2rem 1.4rem",
      background: "var(--surface)",
      border: "1px solid var(--line)",
      borderRadius: "4px",
    }}
    data-testid="values-picker"
  >
    {/* Suggestions */}
    <div className="mb-5">
      <p
        className="font-sans mb-3"
        style={{
          fontSize: "0.66rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--ink-ghost)",
        }}
      >
        {t.step4Suggestions}
        {selectedValues.length >= 3 && (
          <span style={{ marginLeft: "0.6rem", textTransform: "none", letterSpacing: 0, fontStyle: "italic" }}>
            · {t.step4MaxReached}
          </span>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        {t.values
          .filter((v) => !selectedValues.includes(v))
          .map((value) => (
            <button
              key={value}
              data-testid={`value-${value.toLowerCase()}`}
              onClick={() => onToggle(value)}
              disabled={selectedValues.length >= 3}
              className="chip"
              style={
                selectedValues.length >= 3
                  ? { opacity: 0.4, cursor: "not-allowed" }
                  : undefined
              }
            >
              + {value}
            </button>
          ))}
      </div>
    </div>

    {/* Custom value input */}
    <div className="mb-5">
      <div className="flex gap-2">
        <input
          type="text"
          data-testid="custom-value-input"
          value={customValue}
          onChange={(e) => onCustomValueChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAddCustom()}
          placeholder={t.step4CustomPlaceholder}
          disabled={selectedValues.length >= 3}
          className="font-sans flex-1"
          style={{
            background: "transparent",
            border: "1px solid var(--line)",
            borderRadius: "20px",
            padding: "0.35rem 0.8rem",
            fontSize: "0.78rem",
            color: "var(--ink)",
            outline: "none",
          }}
        />
        <button
          onClick={onAddCustom}
          data-testid="add-custom-value-btn"
          disabled={!customValue.trim() || selectedValues.length >= 3}
          className="chip"
          style={{
            background: "var(--sage-soft)",
            borderColor: "var(--sage-border)",
            color: "var(--sage)",
          }}
        >
          {t.step4AddButton}
        </button>
      </div>
    </div>

    {/* Selected values */}
    {selectedValues.length > 0 && (
      <div className="space-y-3 pt-4" style={{ borderTop: "1px solid var(--border-soft)" }}>
        <p
          className="font-sans"
          style={{
            fontSize: "0.66rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "var(--ink-ghost)",
          }}
        >
          {t.step4Selected}
        </p>
        {selectedValues.map((value) => (
          <div key={value} data-testid={`selected-${value.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="mark-ring" />
                <span className="font-serif" style={{ fontSize: "1rem", color: "var(--ink)" }}>
                  {value}
                </span>
              </div>
              <button
                onClick={() => onToggle(value)}
                className="text-xs"
                style={{ color: "var(--ink-ghost)" }}
                aria-label="remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <input
              type="text"
              value={valueReasons[value] || ""}
              onChange={(e) => onUpdateReason(value, e.target.value)}
              placeholder={t.step4WhyPlaceholder}
              className="takeaway-input"
              style={{ marginLeft: "1.6rem", width: "calc(100% - 1.6rem)" }}
            />
          </div>
        ))}
      </div>
    )}

    {/* Continue — v6.1 Sprint 2: Skip is available so users with no matching
        values can proceed without fabricating one. */}
    <div className="mt-6 flex items-center justify-between gap-3">
      <button
        onClick={onSkip}
        data-testid="values-skip-btn"
        className="font-sans"
        style={{
          background: "transparent",
          color: "var(--ink-ghost)",
          padding: "0.55rem 0.6rem",
          fontSize: "0.78rem",
          letterSpacing: "0.04em",
          cursor: "pointer",
          border: "none",
          textDecoration: "underline",
          textUnderlineOffset: "3px",
        }}
      >
        {t.step4Skip}
      </button>
      <button
        onClick={onContinue}
        disabled={!canContinue}
        data-testid="values-continue-btn"
        className="font-sans inline-flex items-center gap-2 transition-all"
        style={{
          background: canContinue ? "var(--ink)" : "var(--line)",
          color: canContinue ? "var(--bg)" : "var(--ink-ghost)",
          padding: "0.55rem 1rem",
          borderRadius: "2px",
          fontSize: "0.78rem",
          letterSpacing: "0.04em",
          cursor: canContinue ? "pointer" : "not-allowed",
        }}
      >
        {t.step4Button} →
      </button>
    </div>
  </div>
);

/* ───────── Summary block ───────── */
const SummaryBlock = ({ summary, t, onStartOver, isSurveyCircle, surveyCode, redeemUrl }) => {
  const [codeCopied, setCodeCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(surveyCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const sectionStyle = {
    padding: "1.4rem 1.5rem",
    background: "var(--surface)",
    border: "1px solid var(--border-soft)",
    borderRadius: "4px",
  };

  const labelStyle = {
    fontSize: "0.62rem",
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    color: "var(--ink-ghost)",
  };

  return (
    <div className="space-y-4 animate-enter" data-testid="summary-block">
      {/* Opening — v5.1 Reflection Copy Refinement: interpretive "opening"
          sentence intentionally suppressed at render time so the summary
          begins with observations. LLM still returns it (no prompt change);
          we simply do not render it. */}
      {summary.opening_line2 && (
        <div style={sectionStyle} data-testid="summary-opening">
          <p
            className="font-serif"
            data-testid="summary-opening-line"
            style={{ fontSize: "1.05rem", color: "var(--ink)", lineHeight: 1.7, fontWeight: 400 }}
          >
            {summary.opening_line2}
          </p>
        </div>
      )}

      {/* Tension — v6.1 Sprint 2: render only when two grounded sides exist */}
      {summary.tension_options?.length >= 2 && (
        <div style={sectionStyle} data-testid="summary-tension">
          <p className="font-sans mb-3" style={labelStyle}>
            {t.summaryTensionTitle}
          </p>
          <ul className="space-y-2 mb-3">
            {summary.tension_options.map((opt, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mark-ring" style={{ marginTop: "0.25rem" }} />
                <span className="font-serif" style={{ fontSize: "1rem", color: "var(--ink)" }}>
                  {opt}
                </span>
              </li>
            ))}
          </ul>
          {summary.tension_middle && (
            <p
              className="font-sans"
              style={{ fontSize: "0.84rem", color: "var(--ink-mid)", fontStyle: "italic", lineHeight: 1.7 }}
            >
              {summary.tension_middle}
            </p>
          )}
        </div>
      )}

      {/* What this is about — v6.1 Sprint 2: render only when grounded values exist */}
      {summary.values?.length >= 1 && (
        <div style={sectionStyle} data-testid="summary-about">
          <p className="font-sans mb-3" style={labelStyle}>
            {t.summaryAboutTitle}
          </p>
          {summary.about_intro && (
            <p
              className="font-sans mb-3"
              style={{ fontSize: "0.92rem", color: "var(--ink-mid)", lineHeight: 1.7 }}
            >
              {summary.about_intro}
            </p>
          )}
          <ul className="space-y-2 mb-3">
            {summary.values.map((v, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mark-ring" style={{ marginTop: "0.25rem" }} />
                <span className="font-serif" style={{ fontSize: "0.98rem", color: "var(--ink)" }}>
                  {v}
                </span>
              </li>
            ))}
          </ul>
          {summary.about_outro && (
            <p
              className="font-sans"
              style={{ fontSize: "0.84rem", color: "var(--ink-mid)", fontStyle: "italic" }}
            >
              {summary.about_outro}
            </p>
          )}
        </div>
      )}

      {/* Trade-off — v6.1 Sprint 2: render only when at least one grounded option exists */}
      {summary.tradeoff_options?.length >= 1 && (
        <div style={sectionStyle} data-testid="summary-tradeoff">
          <p className="font-sans mb-3" style={labelStyle}>
            {t.summaryTradeoffTitle}
          </p>
          {summary.tradeoff_intro && (
            <p
              className="font-sans mb-3"
              style={{ fontSize: "0.92rem", color: "var(--ink-mid)", lineHeight: 1.7 }}
            >
              {summary.tradeoff_intro}
            </p>
          )}
          <ul className="space-y-2">
            {summary.tradeoff_options.map((opt, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mark-ring" style={{ marginTop: "0.25rem" }} />
                <span className="font-serif" style={{ fontSize: "0.98rem", color: "var(--ink)" }}>
                  {opt}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Direction — v6.1 Sprint 2: render only when there's a direction OR a grounded observation.
          Heading toggles between "Where you seem to lean" (explicit direction with hesitation)
          and "What stands out" (neutral observation). */}
      {(summary.direction || summary.direction_intro) && (
        <div style={sectionStyle} data-testid="summary-direction">
          <p className="font-sans mb-3" style={labelStyle}>
            {summary.direction_hesitation
              ? t.summaryDirectionTitle
              : t.summaryObservationTitle}
          </p>
          {summary.direction_intro && (
            <p
              className="font-sans mb-2"
              style={{ fontSize: "0.92rem", color: "var(--ink-mid)", lineHeight: 1.7 }}
            >
              {summary.direction_intro}
            </p>
          )}
          {summary.direction && (
            <p
              className="font-serif mb-2"
              style={{ fontSize: "1.1rem", color: "var(--ink)", fontStyle: "italic" }}
            >
              {summary.direction_hesitation ? `→ ${summary.direction}` : summary.direction}
            </p>
          )}
          {summary.direction_hesitation && (
            <p
              className="font-sans"
              style={{ fontSize: "0.84rem", color: "var(--ink-mid)", lineHeight: 1.7 }}
            >
              {summary.direction_hesitation}
            </p>
          )}
        </div>
      )}

      {/* Reflection — v6.1 Sprint 2: render only when a grounded reflection question exists */}
      {summary.reflection_question && (
        <div
          style={{
            ...sectionStyle,
            background: "var(--sage-soft)",
            borderColor: "var(--sage-border)",
          }}
          data-testid="summary-reflection"
        >
          <p
            className="font-sans mb-3"
            style={{ ...labelStyle, color: "var(--sage)" }}
          >
            {t.summaryReflectionTitle}
          </p>
          {summary.reflection_intro && (
            <p
              className="font-sans mb-3"
              style={{ fontSize: "0.9rem", color: "var(--ink-mid)", lineHeight: 1.7 }}
            >
              {summary.reflection_intro}
            </p>
          )}
          <p
            className="font-serif"
            style={{
              fontSize: "1.2rem",
              color: "var(--ink)",
              fontStyle: "italic",
              lineHeight: 1.55,
              marginBottom: "0.75rem",
            }}
          >
            “{summary.reflection_question}”
          </p>
          {summary.reflection_outro && (
            <p
              className="font-sans"
              style={{ fontSize: "0.82rem", color: "var(--ink-mid)" }}
            >
              {summary.reflection_outro}
            </p>
          )}
        </div>
      )}

      {/* Closing */}
      <div style={{ ...sectionStyle, background: "var(--bg-warm)" }}>
        <p
          className="font-serif"
          style={{ fontSize: "1.05rem", color: "var(--ink)", lineHeight: 1.65 }}
        >
          {summary.closing1}
        </p>
        <p
          className="font-sans mt-2"
          style={{ fontSize: "0.92rem", color: "var(--ink-mid)", lineHeight: 1.7 }}
        >
          {summary.closing2}
        </p>
        <p
          className="font-sans mt-3"
          style={{ fontSize: "0.84rem", color: "var(--ink-ghost)", fontStyle: "italic" }}
        >
          {summary.closing_optional}
        </p>
      </div>

      {/* Before you go */}
      <div className="pt-4 mt-4" style={{ borderTop: "1px solid var(--border-soft)" }}>
        <p
          className="font-sans mb-2"
          style={{
            fontSize: "0.62rem",
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--ink-ghost)",
          }}
        >
          {t.beforeYouGo}
        </p>
        <p
          className="font-serif italic mb-4"
          style={{ fontSize: "0.95rem", color: "var(--ink-mid)", lineHeight: 1.7, maxWidth: "52ch" }}
        >
          {t.beforeYouGoText}
        </p>
        <button
          data-testid="structure-another-btn"
          onClick={onStartOver}
          className="font-sans"
          style={{
            background: "transparent",
            border: "1px solid var(--line)",
            borderRadius: "2px",
            padding: "0.5rem 0.9rem",
            fontSize: "0.78rem",
            color: "var(--ink-mid)",
            cursor: "pointer",
            letterSpacing: "0.04em",
          }}
        >
          {t.structureAnother} →
        </button>
      </div>

      {/* SurveyCircle */}
      {isSurveyCircle && (
        <div
          className="pt-5 mt-2"
          style={{ borderTop: "1px solid var(--border-soft)" }}
          data-testid="surveycircle-section"
        >
          <p
            className="font-sans mb-2"
            style={{
              fontSize: "0.62rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "var(--ink-ghost)",
            }}
          >
            {t.surveyCircleTitle}
          </p>
          <p
            className="font-sans mb-3"
            style={{ fontSize: "0.86rem", color: "var(--ink-mid)", lineHeight: 1.7, maxWidth: "52ch" }}
          >
            {t.surveyCircleText}
          </p>
          <div
            className="font-mono inline-block"
            style={{
              background: "var(--bg-warm)",
              border: "1px solid var(--line)",
              padding: "0.5rem 0.85rem",
              fontSize: "0.86rem",
              letterSpacing: "0.05em",
              color: "var(--ink)",
              borderRadius: "2px",
              marginBottom: "0.85rem",
            }}
            data-testid="survey-code"
          >
            {surveyCode}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="copy-code-btn"
              onClick={handleCopy}
              className="chip inline-flex items-center gap-1.5"
            >
              {codeCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {codeCopied ? t.surveyCircleCopied : t.surveyCircleCopy}
            </button>
            <a
              href={redeemUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="redeem-code-btn"
              className="chip inline-flex items-center gap-1.5"
              style={{
                background: "var(--ink)",
                color: "var(--bg)",
                borderColor: "var(--ink)",
              }}
            >
              {t.surveyCircleRedeem}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────── Feedback Panel (testing phase) ───────── */
const ClarityQuestion = ({ t, phase, onSubmit }) => {
  const [score, setScore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const title = phase === "before" ? t.clarityBeforeTitle : t.clarityAfterTitle;

  const submit = async (val) => {
    if (submitting || submitted) return;
    setSubmitting(true);
    await onSubmit(val);
    setSubmitted(true);
  };

  const isLocked = submitting || submitted;  return (
    <div
      className="animate-enter"
      data-testid={`clarity-${phase}-block`}
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
    >
      <p
        className="font-serif"
        data-testid={`clarity-${phase}-title`}
        style={{
          fontSize: "1rem",
          color: "var(--ink)",
          lineHeight: 1.6,
          fontWeight: 500,
          marginBottom: "1rem",
        }}
      >
        {title}
      </p>

      <div style={{ position: "relative", padding: "0.25rem 0" }}>
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={score ?? 5}
          onChange={(e) => setScore(parseInt(e.target.value, 10))}
          onInput={(e) => setScore(parseInt(e.target.value, 10))}
          data-testid={`clarity-${phase}-slider`}
          aria-label={title}
          style={{
            width: "100%",
            accentColor: "var(--ink)",
            opacity: score === null ? 0.55 : 1,
          }}
        />
        <div
          className="flex justify-between font-sans"
          style={{
            marginTop: "0.35rem",
            fontSize: "0.72rem",
            color: "var(--ink-ghost)",
            fontStyle: "italic",
          }}
        >
          <span data-testid={`clarity-${phase}-low`}>1 · {t.clarityScaleLow}</span>
          <span
            className="font-mono"
            data-testid={`clarity-${phase}-value`}
            style={{
              color: score === null ? "var(--ink-ghost)" : "var(--ink)",
              fontStyle: "normal",
              minWidth: "1.5rem",
              textAlign: "center",
            }}
          >
            {score === null ? "—" : score}
          </span>
          <span data-testid={`clarity-${phase}-high`}>{t.clarityScaleHigh} · 10</span>
        </div>
      </div>

      <div
        className="flex items-center justify-between"
        style={{ marginTop: "1.25rem" }}
      >
        <button
          type="button"
          data-testid={`clarity-${phase}-skip-btn`}
          onClick={() => submit(null)}
          disabled={isLocked}
          className="font-sans"
          style={{
            background: "transparent",
            color: "var(--ink-ghost)",
            padding: "0.3rem 0.1rem",
            border: "none",
            fontSize: "0.78rem",
            letterSpacing: "0.04em",
            cursor: isLocked ? "not-allowed" : "pointer",
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            opacity: isLocked ? 0.5 : 1,
          }}
        >
          {t.claritySkip}
        </button>
        <button
          type="button"
          data-testid={`clarity-${phase}-continue-btn`}
          onClick={() => submit(score)}
          disabled={isLocked || score === null}
          className="font-sans"
          style={{
            background: score === null || isLocked ? "var(--line)" : "var(--ink)",
            color: score === null || isLocked ? "var(--ink-ghost)" : "var(--card)",
            padding: "0.55rem 1rem",
            borderRadius: "2px",
            fontSize: "0.8rem",
            letterSpacing: "0.04em",
            border: "none",
            cursor: isLocked || score === null ? "not-allowed" : "pointer",
          }}
        >
          {t.clarityContinue}
        </button>
      </div>
    </div>
  );
};

const ConversationFitBlock = ({ t, stopped, onTryAgain, onStop }) => {
  return (
    <div
      className="animate-enter space-y-4"
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
      data-testid="conv-fit-block"
    >
      <p
        className="font-serif"
        data-testid="conv-fit-msg-1"
        style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
      >
        {t.convFitMsg1}
      </p>
      <p
        className="font-serif"
        data-testid="conv-fit-msg-2"
        style={{
          fontSize: "0.95rem",
          color: "var(--ink-mid)",
          lineHeight: 1.7,
          fontStyle: "italic",
        }}
      >
        {t.convFitMsg2}
      </p>

      {stopped ? (
        <p
          className="font-serif animate-enter"
          data-testid="conv-fit-closing"
          style={{
            marginTop: "1rem",
            paddingTop: "0.9rem",
            borderTop: "1px solid var(--line-soft)",
            fontSize: "0.9rem",
            color: "var(--ink-mid)",
            lineHeight: 1.65,
            fontStyle: "italic",
          }}
        >
          {t.convFitClosing}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            data-testid="conv-fit-try-again-btn"
            onClick={onTryAgain}
            className="font-sans inline-flex items-center gap-2"
            style={{
              background: "var(--ink)",
              color: "var(--card)",
              padding: "0.55rem 1rem",
              borderRadius: "2px",
              fontSize: "0.8rem",
              letterSpacing: "0.04em",
              border: "none",
              cursor: "pointer",
            }}
          >
            {t.convFitTryAgain}
          </button>
          <button
            type="button"
            data-testid="conv-fit-stop-btn"
            onClick={onStop}
            className="chip"
          >
            {t.convFitStop}
          </button>
        </div>
      )}
    </div>
  );
};

/* ───────── Signal-driven block (v6.3 Sprint 3B) ─────────
 * A single component that renders one of the four grounded responses defined
 * in Sprint 3B. It never psychologises the user or interprets meta-comments —
 * copy comes verbatim from LanguageContext.
 */
const SignalBlock = ({
  t,
  variant,
  hasGroundedMaterial,
  completed,
  onMismatchSkip,
  onMismatchTryAgain,
  onStopFinish,
  onGnhStopHere,
  onGnhStartAgain,
  onSaturationFinish,
}) => {
  // v6.4 Sprint 3C — local latch that hides the saturation Finish button
  // after first click, keeping the observation body visible (unlike the
  // 'completed' prop which swaps in the convFitClosing line).
  const [saturationFinished, setSaturationFinished] = useState(false);
  // v6.4 Sprint 3C — saturation renders one of two grounded completion states.
  // A: enough grounded material → one grounded observation + calm closing.
  // B: insufficient material    → calm closing only.
  const isSaturation = variant === "saturation";
  const message = isSaturation
    ? hasGroundedMaterial
      ? null // observation is rendered separately below
      : t.signalSaturationMinimalMsg
    : variant === "explicit_stop"
    ? t.signalStopMsg
    : variant === "global_not_helping"
    ? t.signalGnhMsg
    : t.signalMismatchMsg;

  return (
    <div
      className="animate-enter space-y-4"
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
      data-testid={`signal-block-${variant}`}
    >
      {isSaturation && hasGroundedMaterial ? (
        <div className="space-y-2" data-testid="signal-saturation-observation">
          <p
            className="font-sans"
            style={{
              fontSize: "0.62rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "var(--ink-ghost)",
            }}
          >
            {t.signalSaturationObservationTitle}
          </p>
          <p
            className="font-serif"
            style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
          >
            {t.signalSaturationObservationText}
          </p>
          <p
            className="font-sans"
            style={{ fontSize: "0.9rem", color: "var(--ink-mid)", lineHeight: 1.7, fontStyle: "italic" }}
          >
            {t.signalSaturationClosing}
          </p>
        </div>
      ) : (
        <p
          className="font-serif"
          data-testid="signal-block-msg"
          style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
        >
          {message}
        </p>
      )}

      {completed ? (
        <p
          className="font-serif animate-enter"
          data-testid="signal-block-closing"
          style={{
            marginTop: "1rem",
            paddingTop: "0.9rem",
            borderTop: "1px solid var(--line-soft)",
            fontSize: "0.9rem",
            color: "var(--ink-mid)",
            lineHeight: 1.65,
            fontStyle: "italic",
          }}
        >
          {t.convFitClosing}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 pt-2">
          {variant === "mismatch" && (
            <>
              <button
                type="button"
                data-testid="signal-mismatch-skip-btn"
                onClick={onMismatchSkip}
                className="font-sans inline-flex items-center gap-2"
                style={{
                  background: "var(--ink)",
                  color: "var(--card)",
                  padding: "0.55rem 1rem",
                  borderRadius: "2px",
                  fontSize: "0.8rem",
                  letterSpacing: "0.04em",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {t.signalMismatchSkip}
              </button>
              <button
                type="button"
                data-testid="signal-mismatch-try-again-btn"
                onClick={onMismatchTryAgain}
                className="chip"
              >
                {t.signalMismatchTryAgain}
              </button>
            </>
          )}
          {variant === "explicit_stop" && (
            <button
              type="button"
              data-testid="signal-stop-finish-btn"
              onClick={onStopFinish}
              className="font-sans inline-flex items-center gap-2"
              style={{
                background: "var(--ink)",
                color: "var(--card)",
                padding: "0.55rem 1rem",
                borderRadius: "2px",
                fontSize: "0.8rem",
                letterSpacing: "0.04em",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t.signalStopFinish}
            </button>
          )}
          {variant === "global_not_helping" && (
            <>
              <button
                type="button"
                data-testid="signal-gnh-stop-btn"
                onClick={onGnhStopHere}
                className="font-sans inline-flex items-center gap-2"
                style={{
                  background: "var(--ink)",
                  color: "var(--card)",
                  padding: "0.55rem 1rem",
                  borderRadius: "2px",
                  fontSize: "0.8rem",
                  letterSpacing: "0.04em",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {t.signalGnhStopHere}
              </button>
              <button
                type="button"
                data-testid="signal-gnh-restart-btn"
                onClick={onGnhStartAgain}
                className="chip"
              >
                {t.signalGnhStartAgain}
              </button>
            </>
          )}
          {variant === "saturation" && !saturationFinished && (
            <button
              type="button"
              data-testid="signal-saturation-finish-btn"
              onClick={() => {
                setSaturationFinished(true);
                onSaturationFinish?.();
              }}
              className="font-sans inline-flex items-center gap-2"
              style={{
                background: "var(--ink)",
                color: "var(--card)",
                padding: "0.55rem 1rem",
                borderRadius: "2px",
                fontSize: "0.8rem",
                letterSpacing: "0.04em",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t.signalSaturationFinish}
            </button>
          )}
        </div>
      )}
    </div>
  );
};


/* ───────── Reflection Fit boundary block (v6.7 Sprint 4B) ─────────
 * Renders when the initial request is clearly asking for an external
 * answer / causal explanation rather than reflection. Two calm choices:
 *   - "Reflect on what this brings up for me" → transition question flow
 *   - "Leave it here"                         → clean exit
 * No user categorisation is shown. Copy is verbatim from LanguageContext.
 */
const ReflectionFitBlock = ({ t, completed, onReflect, onLeave }) => {
  return (
    <div
      className="animate-enter space-y-4"
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
      data-testid="reflection-fit-block"
    >
      <p
        className="font-serif"
        data-testid="reflection-fit-msg-1"
        style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
      >
        {t.reflectionFitMsg1}
      </p>
      <p
        className="font-serif"
        data-testid="reflection-fit-msg-2"
        style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
      >
        {t.reflectionFitMsg2}
      </p>

      {completed ? (
        <p
          className="font-serif animate-enter"
          data-testid="reflection-fit-closing"
          style={{
            marginTop: "1rem",
            paddingTop: "0.9rem",
            borderTop: "1px solid var(--line-soft)",
            fontSize: "0.9rem",
            color: "var(--ink-mid)",
            lineHeight: 1.65,
            fontStyle: "italic",
          }}
        >
          {t.reflectionFitLeaveClosing}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            data-testid="reflection-fit-reflect-btn"
            onClick={onReflect}
            className="font-sans inline-flex items-center gap-2"
            style={{
              background: "var(--ink)",
              color: "var(--card)",
              padding: "0.55rem 1rem",
              borderRadius: "2px",
              fontSize: "0.8rem",
              letterSpacing: "0.04em",
              border: "none",
              cursor: "pointer",
            }}
          >
            {t.reflectionFitReflect}
          </button>
          <button
            type="button"
            data-testid="reflection-fit-leave-btn"
            onClick={onLeave}
            className="chip"
            style={{ minHeight: "44px" }}
          >
            {t.reflectionFitLeave}
          </button>
        </div>
      )}
    </div>
  );
};


/* ───────── Pre-summary measurement checkpoint (v6.9 Sprint 5A) ─────────
 * A single research question shown AFTER step 5 direction and BEFORE the
 * summary is generated. The 4 canonical answers are stored via
 * /api/pre-summary-change; open text is only shown for a_little/yes. NONE
 * of this data influences generation — see server-side extra="forbid" on
 * SummaryInput for the construction-level guarantee.
 */
const PreSummaryCheckpoint = ({ t, submitted, onSubmit }) => {
  const [choice, setChoice] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const needsText = choice === "a_little" || choice === "yes";

  const submit = async (finalChoice, finalText) => {
    if (busy) return;
    setBusy(true);
    try {
      await onSubmit(finalChoice, finalText);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="animate-enter space-y-4"
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
      data-testid="pre-summary-checkpoint"
    >
      <p
        className="font-serif"
        data-testid="pre-summary-question"
        style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
      >
        {t.preSummaryQuestion}
      </p>

      {!submitted && (
        <div className="flex flex-wrap gap-2 pt-1">
          {[
            { key: "no", label: t.preSummaryOptionNo },
            { key: "a_little", label: t.preSummaryOptionALittle },
            { key: "yes", label: t.preSummaryOptionYes },
            { key: "unsure", label: t.preSummaryOptionUnsure },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              data-testid={`pre-summary-option-${opt.key}`}
              disabled={busy}
              onClick={async () => {
                setChoice(opt.key);
                if (opt.key === "no" || opt.key === "unsure") {
                  await submit(opt.key, "");
                }
              }}
              className="chip"
              style={{
                minHeight: "44px",
                background: choice === opt.key ? "var(--ink)" : undefined,
                color: choice === opt.key ? "var(--card)" : undefined,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {!submitted && needsText && (
        <div className="space-y-3 pt-2 animate-enter">
          <label
            className="font-sans"
            style={{
              fontSize: "0.85rem",
              color: "var(--ink-mid)",
              display: "block",
            }}
          >
            {t.preSummaryFollowUp}
          </label>
          <textarea
            data-testid="pre-summary-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t.preSummaryFollowUpPlaceholder}
            rows={3}
            className="font-serif"
            style={{
              width: "100%",
              padding: "0.75rem 0.9rem",
              border: "1px solid var(--line-soft)",
              borderRadius: "var(--r-md)",
              fontSize: "0.95rem",
              lineHeight: 1.6,
              resize: "vertical",
              background: "var(--card)",
              color: "var(--ink)",
            }}
          />
          <button
            type="button"
            data-testid="pre-summary-continue-btn"
            disabled={busy}
            onClick={() => submit(choice, text)}
            className="font-sans inline-flex items-center gap-2"
            style={{
              background: "var(--ink)",
              color: "var(--card)",
              padding: "0.55rem 1rem",
              borderRadius: "2px",
              fontSize: "0.8rem",
              letterSpacing: "0.04em",
              border: "none",
              cursor: "pointer",
              minHeight: "44px",
            }}
          >
            {t.preSummaryContinue}
          </button>
        </div>
      )}
    </div>
  );
};


/* ───── Reported change-stage checkpoint (v6.10 Sprint 5B) ─────
 * Single research question shown AFTER clarity_after and BEFORE feedback.
 * Five canonical option buttons; no free text. Same visual pattern as the
 * Sprint 5A checkpoint. Stored via /api/reported-change-stage in its own
 * collection; never enters generation input.
 */
const ReportedChangeStageCheckpoint = ({ t, submitted, onSubmit }) => {
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const options = [
    { key: "during_questions", label: t.reportedChangeStageDuringQuestions },
    { key: "summary", label: t.reportedChangeStageSummary },
    { key: "both", label: t.reportedChangeStageBoth },
    { key: "no_change", label: t.reportedChangeStageNoChange },
    { key: "unsure", label: t.reportedChangeStageUnsure },
  ];

  return (
    <div
      className="animate-enter space-y-4"
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
      data-testid="reported-change-stage-checkpoint"
    >
      <p
        className="font-serif"
        data-testid="reported-change-stage-question"
        style={{ fontSize: "1rem", color: "var(--ink)", lineHeight: 1.7 }}
      >
        {t.reportedChangeStageQuestion}
      </p>

      {!submitted && (
        <div className="flex flex-wrap gap-2 pt-1">
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              data-testid={`reported-change-stage-option-${opt.key}`}
              disabled={busy}
              onClick={async () => {
                if (busy) return;
                setBusy(true);
                setChoice(opt.key);
                try {
                  await onSubmit(opt.key);
                } finally {
                  setBusy(false);
                }
              }}
              className="chip"
              style={{
                minHeight: "44px",
                background: choice === opt.key ? "var(--ink)" : undefined,
                color: choice === opt.key ? "var(--card)" : undefined,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const FeedbackPanel = ({ t, sessionId, language, trafficSource, onSubmitted }) => {
  const [meaningful, setMeaningful] = useState("");
  const [helpfulness, setHelpfulness] = useState(null);
  const [confusion, setConfusion] = useState("");
  const [description, setDescription] = useState("");
  const [reuse, setReuse] = useState(null);
  const [optional, setOptional] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Prolific redirect countdown (only starts AFTER a successful POST /api/feedback)
  const isProlific = !!(trafficSource && trafficSource.isProlific);
  const prolificRedirectUrl = PROLIFIC_COMPLETION_CODE
    ? `${PROLIFIC_REDIRECT_BASE}?cc=${encodeURIComponent(PROLIFIC_COMPLETION_CODE)}`
    : null;
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Always cancel any pending redirect on unmount.
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, []);

  const hasAnyReflection =
    meaningful.trim().length > 0 ||
    confusion.trim().length > 0 ||
    description.trim().length > 0;
  const isComplete = helpfulness !== null && reuse !== null && hasAnyReflection;

  const handleSubmit = async () => {
    if (submitting || submitted) return; // prevent duplicate submissions
    if (!isComplete) {
      setShowHint(true);
      return;
    }
    setShowHint(false);
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        session_id: sessionId,
        language,
        helpfulness,
        meaningful_moment: meaningful,
        confusion,
        description,
        reuse_intention: reuse,
        optional_comments: optional,
        // Legacy field kept for backward-compat with older reports/queries.
        source: trafficSource ? trafficSource.source : null,
        // v2.1 traffic-source metadata (all optional server-side).
        traffic_source: trafficSource ? trafficSource.source : null,
        prolific_pid: trafficSource ? trafficSource.prolificPid : null,
        study_id: trafficSource ? trafficSource.studyId : null,
        prolific_session_id: trafficSource ? trafficSource.prolificSessionId : null,
      });

      // Only NOW that feedback is persisted do we allow the Prolific redirect timer.
      setSubmitted(true);
      if (onSubmitted) onSubmitted();

      if (isProlific && prolificRedirectUrl) {
        redirectTimerRef.current = setTimeout(() => {
          // Same tab, per spec.
          window.location.href = prolificRedirectUrl;
        }, PROLIFIC_REDIRECT_DELAY_MS);
      }
    } catch (e) {
      // Do NOT redirect on failure — keep participant inside normal retry flow.
      console.error("feedback submit error", e);
      setSubmitting(false);
      toast.error(t.errorGeneric || "Something went wrong. Please try again.");
      return;
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = {
    fontSize: "0.62rem",
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    color: "var(--ink-ghost)",
  };

  const questionStyle = {
    fontSize: "1rem",
    color: "var(--ink)",
    lineHeight: 1.55,
    fontWeight: 500,
    marginBottom: "0.75rem",
  };

  const textareaStyle = {
    width: "100%",
    background: "var(--card)",
    border: "1px solid var(--line)",
    borderRadius: "var(--r)",
    padding: "0.65rem 0.8rem",
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.88rem",
    color: "var(--ink)",
    outline: "none",
    resize: "none",
    lineHeight: 1.6,
  };

  if (submitted) {
    return (
      <div
        className="animate-enter"
        style={{
          padding: "1.25rem 1.5rem",
          background: "var(--bg-warm)",
          border: "1px solid var(--line-soft)",
          borderRadius: "var(--r-lg)",
        }}
        data-testid="feedback-thanks"
      >
        <p
          className="font-serif"
          style={{
            fontStyle: "italic",
            fontSize: "1rem",
            color: "var(--ink-mid)",
            lineHeight: 1.65,
          }}
        >
          {t.feedbackThanks}
        </p>

        {isProlific && prolificRedirectUrl && (
          <div
            className="animate-enter"
            data-testid="prolific-completion"
            style={{
              marginTop: "1.25rem",
              paddingTop: "1.1rem",
              borderTop: "1px solid var(--line-soft)",
            }}
          >
            <p
              className="font-serif"
              data-testid="prolific-confirm-1"
              style={{ fontSize: "0.95rem", color: "var(--ink)", lineHeight: 1.65 }}
            >
              {t.prolificConfirm1}
            </p>
            <p
              className="font-serif"
              data-testid="prolific-confirm-2"
              style={{
                marginTop: "0.4rem",
                fontSize: "0.9rem",
                color: "var(--ink-mid)",
                lineHeight: 1.65,
                fontStyle: "italic",
              }}
            >
              {t.prolificConfirm2}
            </p>
            <a
              href={prolificRedirectUrl}
              data-testid="prolific-return-btn"
              className="font-sans inline-flex items-center gap-2"
              style={{
                marginTop: "1rem",
                background: "var(--ink)",
                color: "var(--card)",
                padding: "0.6rem 1.1rem",
                borderRadius: "2px",
                fontSize: "0.82rem",
                letterSpacing: "0.04em",
                border: "none",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              {t.prolificReturnBtn} →
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="animate-enter space-y-6"
      style={{
        padding: "1.4rem 1.5rem",
        background: "var(--surface)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r-lg)",
      }}
      data-testid="feedback-panel"
    >
      {/* Intro */}
      <p
        className="font-serif"
        data-testid="feedback-intro"
        style={{
          fontSize: "0.94rem",
          color: "var(--ink-mid)",
          lineHeight: 1.7,
        }}
      >
        {t.feedbackIntro}
      </p>

      {/* Q1 — Meaningful moment */}
      <div>
        <p className="font-serif" style={questionStyle}>
          {t.feedbackQ1}
        </p>
        <textarea
          data-testid="feedback-q1-meaningful"
          value={meaningful}
          onChange={(e) => setMeaningful(e.target.value)}
          placeholder={t.feedbackQ1Placeholder}
          rows={3}
          maxLength={2000}
          style={textareaStyle}
        />
      </div>

      {/* Q2 — Helpfulness (1-10) */}
      <div>
        <p className="font-serif" style={questionStyle}>
          {t.feedbackQ2}
        </p>
        <div
          className="flex flex-wrap gap-1.5"
          data-testid="feedback-q2-helpfulness"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`feedback-q2-${n}`}
              onClick={() => setHelpfulness(n)}
              className={`chip ${helpfulness === n ? "active" : ""}`}
              style={{ minWidth: "2.25rem", justifyContent: "center" }}
            >
              {n}
            </button>
          ))}
        </div>
        <div
          className="flex justify-between font-sans"
          style={{
            marginTop: "0.5rem",
            fontSize: "0.72rem",
            color: "var(--ink-ghost)",
            fontStyle: "italic",
          }}
        >
          <span>{t.feedbackQ2ScaleLow}</span>
          <span>{t.feedbackQ2ScaleHigh}</span>
        </div>
      </div>

      {/* Q3 — Confusion */}
      <div>
        <p className="font-serif" style={questionStyle}>
          {t.feedbackQ3}
        </p>
        <textarea
          data-testid="feedback-q3-confusion"
          value={confusion}
          onChange={(e) => setConfusion(e.target.value)}
          placeholder={t.feedbackQ3Placeholder}
          rows={3}
          maxLength={2000}
          style={textareaStyle}
        />
      </div>

      {/* Q4 — Description / positioning */}
      <div>
        <p className="font-serif" style={questionStyle}>
          {t.feedbackQ4}
        </p>
        <textarea
          data-testid="feedback-q4-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t.feedbackQ4Placeholder}
          rows={3}
          maxLength={2000}
          style={textareaStyle}
        />
      </div>

      {/* Q5 — Reuse intention */}
      <div>
        <p className="font-serif" style={questionStyle}>
          {t.feedbackQ5}
        </p>
        <div className="flex flex-wrap gap-2" data-testid="feedback-q5-reuse">
          {t.feedbackQ5Options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`feedback-q5-${opt.value}`}
              onClick={() => setReuse(opt.value)}
              className={`chip ${reuse === opt.value ? "active" : ""}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Q6 — Optional comments */}
      <div>
        <p className="font-serif" style={questionStyle}>
          {t.feedbackQ6}
        </p>
        <textarea
          data-testid="feedback-q6-optional"
          value={optional}
          onChange={(e) => setOptional(e.target.value)}
          placeholder={t.feedbackQ6Placeholder}
          rows={2}
          maxLength={2000}
          style={textareaStyle}
        />
      </div>

      {/* Submit row */}
      <div
        className="flex items-center justify-between pt-2"
        style={{ borderTop: "1px solid var(--line-soft)" }}
      >
        <span className="font-sans" style={labelStyle}>
          Testing · one minute
        </span>
        <button
          type="button"
          data-testid="feedback-submit-btn"
          onClick={handleSubmit}
          disabled={submitting}
          className="font-sans inline-flex items-center gap-2"
          style={{
            background: "var(--ink)",
            color: "var(--card)",
            padding: "0.55rem 1rem",
            borderRadius: "2px",
            fontSize: "0.78rem",
            letterSpacing: "0.04em",
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            transition: "background 0.2s",
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {submitting ? "…" : t.feedbackSubmit}
        </button>
      </div>

      {/* Gentle validation hint */}
      {showHint && !isComplete && (
        <p
          className="font-serif animate-enter"
          data-testid="feedback-hint"
          style={{
            marginTop: "-0.25rem",
            fontStyle: "italic",
            fontSize: "0.82rem",
            color: "var(--ink-mid)",
            lineHeight: 1.6,
          }}
        >
          {t.feedbackHint}
        </p>
      )}
    </div>
  );
};

/* ───────── Main DecisionFlow ───────── */
const DecisionFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialContext = location.state?.initialContext || "";
  const { language, t } = useLanguage();
  const { isSurveyCircle, surveyCode, redeemUrl } = useSurveyCircle();
  const trafficSource = useTrafficSource();

  const [turns, setTurns] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0=intro, 1-5 questions, 6=summary
  const [isTyping, setIsTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // v6.3 Sprint 3B — locks send while `/api/detect-signal` is in flight so a
  // second submit cannot race the response.
  const [isDetectingSignal, setIsDetectingSignal] = useState(false);
  // Track the number of mismatch signals seen per step so the second mismatch
  // auto-skips the question without re-showing the clarification.
  const [mismatchCountByStep, setMismatchCountByStep] = useState({});
  // Skipped-question memory: values from these steps are treated as absent
  // (empty string) so the grounded conditional summary can omit sections.
  const [skippedSteps, setSkippedSteps] = useState(() => new Set());
  // Reflects whether the user has confirmed the terminal action for a
  // signal-driven block (Finish / Stop here). Purely visual.
  const [signalCompleted, setSignalCompleted] = useState(false);
  // v6.7 Sprint 4B — Reflection Fit boundary state.
  // - reflectionFitCompleted: user chose "Leave it here" (calm exit).
  // - reflectionFitAwaitingTransition: user chose "Reflect on what this
  //   brings up for me"; the next textarea submission becomes the new
  //   grounded step-1 context. The original request is NOT used as evidence.
  const [reflectionFitCompleted, setReflectionFitCompleted] = useState(false);
  const [reflectionFitAwaitingTransition, setReflectionFitAwaitingTransition] =
    useState(false);
  const reflectionFitActionTakenRef = useRef(false);
  // v6.9 Sprint 5A — pre-summary measurement checkpoint state (research only).
  const [preSummarySubmitted, setPreSummarySubmitted] = useState(false);
  const preSummaryTakenRef = useRef(false);
  // v6.10 Sprint 5B — post-summary reported-change-stage state (research only).
  const [reportedChangeStageSubmitted, setReportedChangeStageSubmitted] = useState(false);
  const reportedChangeStageTakenRef = useRef(false);
  // v6.11 Sprint 5C — passive process/effort measurement timestamps.
  // Refs (not state) so recording never triggers a re-render or changes
  // participant-facing behaviour.
  const sessionStartTsRef = useRef(null);
  const reflectionStartTsRef = useRef(null);
  const preSummaryShownTsRef = useRef(null);
  const summaryShownTsRef = useRef(null);
  const clarityAfterProceedTsRef = useRef(null);
  const metricsSentRef = useRef(false);
  // Monotonic counter used to discard stale async detect-signal responses
  // when a newer user action has already changed the flow state.
  // v6.4 Sprint 3C — idempotency guard for terminal signal actions. Prevents
  // double clicks on Finish / Stop here / Start again from re-logging or
  // inserting duplicate clarity-after blocks.
  const actionTakenRef = useRef(false);
  // v6.4 Sprint 3C — stable session_id across StrictMode / effect re-runs so
  // the landing pre-fill never fires two detect-signal calls with different ids.
  const bootSessionIdRef = useRef(null);
  // Gate the boot pre-fill classifier against StrictMode re-runs.
  const bootDetectionFiredRef = useRef(false);
  const submitGenerationRef = useRef(0);

  const [context, setContext] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [tradeoff, setTradeoff] = useState("");
  const [tradeoffClarified, setTradeoffClarified] = useState(false);
  const [conversationFitStopped, setConversationFitStopped] = useState(false);
  const [clarityBeforeSubmitted, setClarityBeforeSubmitted] = useState(false);
  const [clarityAfterSubmitted, setClarityAfterSubmitted] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [valueReasons, setValueReasons] = useState({});
  const [customValue, setCustomValue] = useState("");
  const [direction, setDirection] = useState("");
  const [summary, setSummary] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  /* ──── load sessions from localStorage ──── */
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("clarity-sessions") || "[]");
      setSessions(stored);
    } catch {
      setSessions([]);
    }
  }, []);

  /* ──── auto-scroll ──── */
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [turns, isTyping]);

  /* ──── initial AI greeting ──── */
  const pushAi = useCallback((content, opts = {}) => {
    setTurns((prev) => [
      ...prev,
      { id: `ai-${Date.now()}-${Math.random()}`, role: "ai", content, italic: opts.italic, helper: opts.helper },
    ]);
  }, []);

  const pushUser = (content) => {
    setTurns((prev) => [
      ...prev,
      { id: `user-${Date.now()}-${Math.random()}`, role: "user", content },
    ]);
  };

  const pushBreath = useCallback(() => {
    setTurns((prev) => [
      ...prev,
      { id: `breath-${Date.now()}-${Math.random()}`, role: "breath" },
    ]);
  }, []);

  const aiTurn = useCallback(
    async (text, opts = {}) => {
      setIsTyping(true);
      await new Promise((r) => setTimeout(r, TYPING_DELAY));
      setIsTyping(false);
      pushAi(text, opts);
      await new Promise((r) => setTimeout(r, BREATH_DELAY));
    },
    [pushAi]
  );

  /* ──── boot conversation ──── */
  // v6.11 Sprint 5C — mark reflection start the first time currentStep
  // reaches 1 or 2. This captures both the landing-pre-fill path (which
  // jumps directly to step 2 after clarity-before) and the fresh-start
  // path (which begins at step 1). Uses a ref so no participant-facing
  // behaviour changes and no re-render loop is triggered.
  useEffect(() => {
    if (
      reflectionStartTsRef.current == null &&
      (currentStep === 1 || currentStep === 2)
    ) {
      reflectionStartTsRef.current = Date.now();
    }
  }, [currentStep]);

  useEffect(() => {
    // v6.4 Sprint 3C — stable session_id via ref so StrictMode / effect
    // re-runs never regenerate the id (which would produce orphan sessions
    // + duplicate detect-signal docs on the landing-pre-fill path).
    if (!bootSessionIdRef.current) {
      bootSessionIdRef.current = `s_${Date.now()}`;
    }
    const sid = bootSessionIdRef.current;
    if (currentSessionId !== sid) setCurrentSessionId(sid);
    // v6.11 Sprint 5C — capture session start once. Same ref-stability trick
    // as bootSessionIdRef so StrictMode double-mount doesn't reset it.
    if (sessionStartTsRef.current == null) {
      sessionStartTsRef.current = Date.now();
    }

    let timeouts = [];
    const schedule = (fn, ms) => {
      const tid = setTimeout(fn, ms);
      timeouts.push(tid);
      return tid;
    };

    const hasInitial = initialContext && initialContext.trim().length > 0;

    if (hasInitial) {
      // v6.3 Sprint 3B — classify the landing pre-fill BEFORE deciding the
      // boot layout. If a stop/gnh/saturation signal fires, show the block
      // instead of the reflection flow. Mismatch on the FIRST utterance is
      // treated as normal reflection content (falling through) — a user has
      // no earlier question to be mismatched with.
      // v6.4 Sprint 3C — gate so StrictMode / effect re-runs never fire two
      // detect-signal calls for the same landing pre-fill.
      if (bootDetectionFiredRef.current) return;
      bootDetectionFiredRef.current = true;
      (async () => {
        let signal = null;
        try {
          const res = await axios.post(`${API}/detect-signal`, {
            text: initialContext,
            language,
            session_id: sid,
            step: 1,
          });
          signal = res?.data?.conversation_signal_detected || null;
        } catch {
          signal = null;
        }
        if (
          signal === "explicit_stop" ||
          signal === "global_not_helping" ||
          signal === "saturation"
        ) {
          // Show the message + block. Do NOT store as context.
          setTurns([
            { id: `user-init-${Date.now()}`, role: "user", content: initialContext },
            { id: "boot-breath-init", role: "breath" },
            {
              id: `signal-boot-${Date.now()}`,
              role: "signal-block",
              variant: signal,
            },
          ]);
          setCurrentStep(0);
          return;
        }
        // v6.7 Sprint 4B — Reflection Fit boundary check. Runs ONLY on the
        // initial input, AFTER stop/global_not_helping/saturation signals so
        // an explicit user wish to stop always wins. Mismatch does not
        // apply to the very first utterance so we still evaluate fit here.
        let fitBoundary = false;
        try {
          const fitRes = await axios.post(`${API}/detect-reflection-fit`, {
            text: initialContext,
            language,
            session_id: sid,
          });
          fitBoundary = !!(fitRes?.data?.reflection_fit_boundary);
        } catch {
          fitBoundary = false;
        }
        if (fitBoundary) {
          setTurns([
            { id: `user-init-${Date.now()}`, role: "user", content: initialContext },
            { id: "boot-breath-init", role: "breath" },
            {
              id: `reflection-fit-${Date.now()}`,
              role: "reflection-fit-block",
            },
          ]);
          setCurrentStep(0);
          return;
        }
        // Normal or mismatch — proceed with existing pre-fill flow.
        setContext(initialContext);
        setTurns([
          { id: `user-init-${Date.now()}`, role: "user", content: initialContext },
          { id: "boot-breath-init", role: "breath" },
          { id: "clarity-before", role: "clarity-before" },
        ]);
        setCurrentStep(0);
      })();
    } else {
      // Standard boot — greeting → breath → clarity-before → step1 question.
      setIsTyping(true);
      schedule(() => {
        setIsTyping(false);
        setTurns((prev) => [
          ...prev,
          { id: "boot-greeting", role: "ai", content: t.greeting || "Welcome." },
        ]);
        schedule(() => {
          setTurns((prev) => [
            ...prev,
            { id: "boot-breath", role: "breath" },
            { id: "clarity-before", role: "clarity-before" },
          ]);
        }, BREATH_DELAY);
      }, TYPING_DELAY);
    }

    return () => {
      timeouts.forEach((tid) => clearTimeout(tid));
    };
  }, []);

  /* ──── persist session metadata when summary appears ──── */
  useEffect(() => {
    if (summary && currentSessionId) {
      const title = (context || "").slice(0, 60).trim() || t.untitledSession || "Untitled";
      const entry = { id: currentSessionId, title, created_at: new Date().toISOString() };
      const next = [entry, ...sessions.filter((s) => s.id !== currentSessionId)].slice(0, 12);
      setSessions(next);
      try {
        localStorage.setItem("clarity-sessions", JSON.stringify(next));
      } catch {
        // ignore
      }
    }
  }, [summary]);

  /* ──── handle send ──── */

  // v6.3 Sprint 3B — advance one step with an empty answer (used when the
  // user skips a mismatch question). Skipped-step values are stored as ""
  // so the grounded conditional summary can omit sections cleanly.
  const advanceStepSkipping = useCallback(
    async (step) => {
      setSkippedSteps((prev) => {
        const next = new Set(prev);
        next.add(step);
        return next;
      });
      if (step === 1) {
        setContext("");
        pushBreath();
        await aiTurn(t.step2Title, { italic: true });
        setCurrentStep(2);
        setTimeout(() => inputRef.current?.focus(), 100);
      } else if (step === 2) {
        setDifficulty("");
        pushBreath();
        await aiTurn(t.step3Title, { italic: true, helper: t.step3Helper });
        setCurrentStep(3);
        setTimeout(() => inputRef.current?.focus(), 100);
      } else if (step === 3) {
        setTradeoff("");
        pushBreath();
        await aiTurn(t.step4Title, { italic: true });
        setTurns((prev) => [
          ...prev,
          { id: `values-${Date.now()}`, role: "values" },
        ]);
        setCurrentStep(4);
      } else if (step === 5) {
        setDirection("");
        pushBreath();
        // Empty direction + preserved difficulty -> grounded conditional summary.
        await generateSummary("", difficulty || "");
      }
    },
    [t, difficulty]
  );

  // v6.3 Sprint 3B — signal dispatchers. Each pushes the user message
  // (research transparency) but does NOT store it as reflection content, then
  // renders the appropriate SignalBlock.
  const dispatchSignal = useCallback(
    async (signal, userValue) => {
      // Always show the user's message in the transcript — never as content.
      pushUser(userValue);
      setDraft("");
      pushBreath();

      // Research-only logging is already handled by /api/detect-signal
      // (single source of truth). No extra session-event write here.

      if (signal === "mismatch") {
        const step = currentStep;
        const stepName =
          { 1: "context", 2: "difficulty", 3: "tradeoff", 5: "direction" }[step] ||
          "context";
        const count = (mismatchCountByStep[step] || 0) + 1;
        setMismatchCountByStep((prev) => ({ ...prev, [step]: count }));
        if (count >= 2) {
          // Second mismatch on the same question — auto-skip, no new clarification.
          // v6.4 Sprint 3C — inline log because the SignalBlock is never shown.
          try {
            await axios.post(`${API}/session-event`, {
              session_id: currentSessionId,
              language,
              event_type: "conversation_signal_action",
              triggering_step: step,
              signal_type: "mismatch",
              signal_step: stepName,
              signal_action: "skipped_question",
              traffic_source: trafficSource ? trafficSource.source : null,
              prolific_pid: trafficSource ? trafficSource.prolificPid : null,
            });
          } catch {
            // research-only, silent
          }
          await advanceStepSkipping(step);
          return;
        }
        setTurns((prev) => [
          ...prev,
          {
            id: `signal-mismatch-${Date.now()}`,
            role: "signal-block",
            variant: "mismatch",
            step,
          },
        ]);
        return;
      }
      if (signal === "explicit_stop") {
        setTurns((prev) => [
          ...prev,
          {
            id: `signal-stop-${Date.now()}`,
            role: "signal-block",
            variant: "explicit_stop",
          },
        ]);
        return;
      }
      if (signal === "global_not_helping") {
        setTurns((prev) => [
          ...prev,
          {
            id: `signal-gnh-${Date.now()}`,
            role: "signal-block",
            variant: "global_not_helping",
          },
        ]);
        return;
      }
      if (signal === "saturation") {
        // v6.4 Sprint 3C — compute "hasGroundedMaterial" once so the block
        // renders the correct completion state (grounded observation vs
        // minimal closing).
        const grounded =
          (context || "").trim().length > 0 ||
          (difficulty || "").trim().length > 0 ||
          (tradeoff || "").trim().length > 0 ||
          selectedValues.length > 0;
        setTurns((prev) => [
          ...prev,
          {
            id: `signal-sat-${Date.now()}`,
            role: "signal-block",
            variant: "saturation",
            hasGroundedMaterial: grounded,
          },
        ]);
      }
    },
    [
      currentStep,
      currentSessionId,
      language,
      trafficSource,
      mismatchCountByStep,
      advanceStepSkipping,
      context,
      difficulty,
      tradeoff,
      selectedValues,
    ]
  );

  // v6.4 Sprint 3C — small helper for research logging. Silent on failure.
  const logSignalAction = useCallback(
    async (signalType, signalStep, signalAction) => {
      try {
        await axios.post(`${API}/session-event`, {
          session_id: currentSessionId,
          language,
          event_type: "conversation_signal_action",
          triggering_step:
            typeof currentStep === "number" && currentStep >= 1 && currentStep <= 6
              ? currentStep
              : null,
          signal_type: signalType,
          signal_step: signalStep,
          signal_action: signalAction,
          traffic_source: trafficSource ? trafficSource.source : null,
          prolific_pid: trafficSource ? trafficSource.prolificPid : null,
        });
      } catch {
        // research logging never blocks the UX
      }
    },
    [currentSessionId, language, currentStep, trafficSource]
  );

  const handleSend = async () => {
    const value = draft.trim();
    if (!value) return;

    // v6.3 Sprint 3B — hard guards: prevent double submit while detection is
    // in flight, and while any signal-driven block is showing.
    if (isDetectingSignal) return;
    if (signalCompleted) return;
    if (reflectionFitCompleted) return;
    if (
      turns.some(
        (tt) =>
          tt.role === "conv-fit" ||
          tt.role === "signal-block" ||
          tt.role === "reflection-fit-block"
      )
    )
      return;

    // v6.3 Sprint 3B — detect signal SYNCHRONOUSLY (awaited). Use a monotonic
    // generation token to discard stale responses.
    const gen = ++submitGenerationRef.current;
    let signal = null;
    if (currentStep >= 1 && currentStep <= 5) {
      setIsDetectingSignal(true);
      try {
        const res = await axios.post(`${API}/detect-signal`, {
          text: value,
          language,
          session_id: currentSessionId,
          step: currentStep,
        });
        signal = res?.data?.conversation_signal_detected || null;
      } catch {
        // Detection failure never blocks the session — fall through to normal flow.
        signal = null;
      }
      setIsDetectingSignal(false);
      if (gen !== submitGenerationRef.current) return; // stale response
    }

    if (signal) {
      await dispatchSignal(signal, value);
      return;
    }

    // v6.7 Sprint 4B — Reflection Fit boundary check.
    // Runs ONLY on the very first utterance (currentStep === 1) and NEVER
    // during step 2–5. Also skipped for the transition answer after the
    // user chose "Reflect Personally" — that answer IS reflection material.
    // Priority: explicit_stop / global_not_helping / saturation win above
    // via the signals check; if we reach here signal was null/mismatch and
    // fit-check is safe to evaluate.
    if (currentStep === 1 && !reflectionFitAwaitingTransition) {
      let fitBoundary = false;
      try {
        const fitRes = await axios.post(`${API}/detect-reflection-fit`, {
          text: value,
          language,
          session_id: currentSessionId,
        });
        fitBoundary = !!(fitRes?.data?.reflection_fit_boundary);
      } catch {
        fitBoundary = false;
      }
      if (fitBoundary) {
        pushUser(value);
        setDraft("");
        pushBreath();
        setTurns((prev) => [
          ...prev,
          {
            id: `reflection-fit-${Date.now()}`,
            role: "reflection-fit-block",
          },
        ]);
        return;
      }
    }

    // v5.1 Sprint 2 — legacy Conversation-Fit Detection (kept as safety net
    // for phrasings not covered by the Sprint 3A classifier).
    if (
      currentStep >= 1 &&
      currentStep <= 5 &&
      !conversationFitStopped &&
      detectConversationFit(value)
    ) {
      pushUser(value);
      setDraft("");
      pushBreath();
      setTurns((prev) => [
        ...prev,
        { id: `conv-fit-${Date.now()}`, role: "conv-fit" },
      ]);
      try {
        await axios.post(`${API}/session-event`, {
          session_id: currentSessionId,
          language,
          event_type: "conversation_fit_stop",
          triggering_step: currentStep,
          traffic_source: trafficSource ? trafficSource.source : null,
          prolific_pid: trafficSource ? trafficSource.prolificPid : null,
        });
      } catch (e) {
        console.warn("session-event log failed", e);
      }
      return;
    }

    if (currentStep === 1) {
      setContext(value);
      pushUser(value);
      setDraft("");
      // v6.7 Sprint 4B — when this answer is the response to the Reflection
      // Fit transition question, clear the latch so subsequent step 2–5
      // sends run through the normal flow.
      if (reflectionFitAwaitingTransition) {
        setReflectionFitAwaitingTransition(false);
      }
      pushBreath();
      await aiTurn(t.step2Title, { italic: true });
      setCurrentStep(2);
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    if (currentStep === 2) {
      setDifficulty(value);
      pushUser(value);
      setDraft("");
      pushBreath();
      await aiTurn(t.step3Title, { italic: true, helper: t.step3Helper });
      setCurrentStep(3);
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    if (currentStep === 3) {
      // v5.1 — restraint: catch body-location / non-answers ONCE and ask for a
      // conceptual trade-off. If the follow-up is still a body answer, we
      // accept it and move on rather than looping.
      if (!tradeoffClarified && isBodyOrNonAnswer(value)) {
        pushUser(value);
        setDraft("");
        pushBreath();
        await aiTurn(t.step3Clarification, { italic: true });
        setTradeoffClarified(true);
        setTimeout(() => inputRef.current?.focus(), 100);
        return;
      }
      setTradeoff(value);
      pushUser(value);
      setDraft("");
      pushBreath();
      await aiTurn(t.step4Title, { italic: true });
      setTurns((prev) => [
        ...prev,
        { id: `values-${Date.now()}`, role: "values" },
      ]);
      setCurrentStep(4);
      return;
    }
    // Step 5 v1.5 — single "landing" question, no sub-step split.
    if (currentStep === 5) {
      setDirection(value);
      pushUser(value);
      setDraft("");
      // v6.9 Sprint 5A — insert the pre-summary measurement checkpoint
      // BEFORE generating the summary. Nothing captured here influences
      // generation input; it is stored in a separate research collection.
      pushBreath();
      // v6.11 Sprint 5C — mark the pre-summary-shown timestamp exactly at
      // the moment the checkpoint turn is inserted.
      if (preSummaryShownTsRef.current == null) {
        preSummaryShownTsRef.current = Date.now();
      }
      setTurns((prev) => [
        ...prev,
        {
          id: `pre-summary-${Date.now()}`,
          role: "pre-summary-checkpoint",
          // Direction/difficulty snapshot so the follow-up generateSummary
          // call after checkpoint submission uses the exact same payload.
          _dir: value,
          _hes: difficulty || "",
        },
      ]);
    }
  };

  /* ──── values step actions ──── */
  const toggleValue = (value) => {
    setSelectedValues((prev) => {
      if (prev.includes(value)) {
        const newReasons = { ...valueReasons };
        delete newReasons[value];
        setValueReasons(newReasons);
        return prev.filter((v) => v !== value);
      }
      if (prev.length >= 3) return prev;
      return [...prev, value];
    });
  };

  const addCustomValue = () => {
    const v = customValue.trim();
    if (v && !selectedValues.includes(v) && selectedValues.length < 3) {
      setSelectedValues((prev) => [...prev, v]);
      setCustomValue("");
    }
  };

  const updateValueReason = (value, reason) => {
    setValueReasons((prev) => ({ ...prev, [value]: reason }));
  };

  const handleValuesContinue = async () => {
    // v6.1 Sprint 2 — the values picker remains in the transcript after the
    // step advances; guard against re-submission that would duplicate step 5.
    if (currentStep !== 4) return;
    if (selectedValues.length === 0) {
      toast.error(t.errorSelectValue);
      return;
    }
    pushUser(
      selectedValues
        .map((v) =>
          valueReasons[v] ? `${v} — ${valueReasons[v]}` : v
        )
        .join(", ")
    );
    pushBreath();
    // Step 5 v1.5 — single "landing" question, no sub-step.
    await aiTurn(t.step5Title, { italic: true });
    setCurrentStep(5);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // v6.1 Sprint 2 — allow skipping the values step. No selection is stored and
  // the backend receives values=[] so the summary can omit the "about" block
  // rather than fabricate values.
  const handleValuesSkip = async () => {
    if (currentStep !== 4) return;
    setSelectedValues([]);
    setValueReasons({});
    pushUser("—");
    pushBreath();
    await aiTurn(t.step5Title, { italic: true });
    setCurrentStep(5);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  /* ──── generate summary ──── */
  const generateSummary = async (dir, hes) => {
    setIsLoading(true);
    setIsTyping(true);
    try {
      const valuesWithReasons = selectedValues.map((v) => ({
        value: v,
        reason: valueReasons[v] || "",
      }));

      const response = await axios.post(`${API}/generate-summary`, {
        // v6.0 — Research Data Integrity: the same internal session_id used
        // for clarity, feedback and session-event is now sent with the
        // reflection so all four records join reliably at export time.
        session_id: currentSessionId,
        context,
        difficulty,
        tradeoff,
        values: valuesWithReasons,
        direction: dir,
        hesitation: hes,
        language,
        traffic_source: trafficSource ? trafficSource.source : null,
        prolific_pid: trafficSource ? trafficSource.prolificPid : null,
        study_id: trafficSource ? trafficSource.studyId : null,
        prolific_session_id: trafficSource ? trafficSource.prolificSessionId : null,
      });

      setIsTyping(false);
      setSummary(response.data.summary);
      // v6.11 Sprint 5C — mark the summary-shown timestamp exactly when
      // the summary turn is inserted (which is when it becomes visible).
      if (summaryShownTsRef.current == null) {
        summaryShownTsRef.current = Date.now();
      }
      setTurns((prev) => [
        ...prev,
        { id: `summary-${Date.now()}`, role: "summary", content: response.data.summary },
      ]);
      setCurrentStep(6);

      // After a soft pause, inject the clarity-after research question. The
      // feedback panel is only revealed once clarity-after resolves (via
      // handleClarityAfterDone) so the two measurements are cleanly separated.
      setTimeout(() => {
        setTurns((prev) => [
          ...prev,
          { id: `breath-clarity-after-${Date.now()}`, role: "breath" },
          { id: `clarity-after-${Date.now()}`, role: "clarity-after" },
        ]);
      }, BREATH_DELAY + 400);
    } catch (err) {
      console.error("Error generating summary:", err);
      setIsTyping(false);
      toast.error(t.errorGeneric);
    } finally {
      setIsLoading(false);
    }
  };

  /* ──── controls ──── */
  const submitClarity = useCallback(
    async (phase, score) => {
      try {
        await axios.post(`${API}/session-clarity`, {
          session_id: currentSessionId,
          phase,
          score, // number 0-10 OR null when skipped
          language,
          traffic_source: trafficSource ? trafficSource.source : null,
          prolific_pid: trafficSource ? trafficSource.prolificPid : null,
        });
      } catch (e) {
        // Research telemetry — never blocks the reflection experience.
        console.warn("session-clarity log failed", e);
      }
    },
    [currentSessionId, language, trafficSource]
  );

  const handleClarityBeforeDone = useCallback(
    async (score) => {
      if (clarityBeforeSubmitted) return;
      setClarityBeforeSubmitted(true);
      await submitClarity("before", score);
      // Resume the intro flow to the first reflection question.
      const hasInitial = context && context.trim().length > 0;
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        if (reflectionFitAwaitingTransition) {
          // v6.7 Sprint 4B — after boundary→Reflect Personally, ask exactly
          // ONE neutral transition question. The user's answer becomes the
          // new grounded step-1 context. The original request is NOT used
          // as evidence for values/tension/trade-off/direction.
          setTurns((prev) => [
            ...prev,
            {
              id: `post-clarity-fit-transition-${Date.now()}`,
              role: "ai",
              content: t.reflectionFitTransition,
              italic: true,
            },
          ]);
          setCurrentStep(1);
        } else if (hasInitial) {
          setTurns((prev) => [
            ...prev,
            { id: "post-clarity-step2", role: "ai", content: t.step2Title, italic: true },
          ]);
          setCurrentStep(2);
        } else {
          setTurns((prev) => [
            ...prev,
            { id: "post-clarity-step1", role: "ai", content: t.step1Title, italic: true },
          ]);
          setCurrentStep(1);
        }
        setTimeout(() => inputRef.current?.focus(), 100);
      }, TYPING_DELAY);
    },
    [clarityBeforeSubmitted, submitClarity, context, t, reflectionFitAwaitingTransition]
  );

  const handleClarityAfterDone = useCallback(
    async (score) => {
      if (clarityAfterSubmitted) return;
      setClarityAfterSubmitted(true);
      // v6.11 Sprint 5C — mark the moment the participant proceeds from
      // the summary to clarity_after. This is a behavioural proxy, NOT
      // proof of reading, per Sprint 5C rule 7.
      if (clarityAfterProceedTsRef.current == null) {
        clarityAfterProceedTsRef.current = Date.now();
      }
      await submitClarity("after", score);
      // v6.10 Sprint 5B — insert the reported-change-stage measurement here.
      // The feedback panel is only revealed once this measurement resolves
      // (via handleReportedChangeStageDone) so the two are cleanly separated.
      setTurns((prev) => [
        ...prev,
        { id: `breath-rcs-${Date.now()}`, role: "breath" },
        { id: `rcs-${Date.now()}`, role: "reported-change-stage-checkpoint" },
      ]);
    },
    [clarityAfterSubmitted, submitClarity]
  );

  // v6.10 Sprint 5B — after the research answer is captured, reveal the
  // existing feedback flow with exactly the same copy as before Sprint 5B.
  const handleReportedChangeStageDone = useCallback(
    async (canonical) => {
      if (reportedChangeStageTakenRef.current) return;
      reportedChangeStageTakenRef.current = true;
      // Fire-and-forget with an explicit timeout — matches the Sprint 5A
      // pattern so a hanging research endpoint cannot stall completion.
      try {
        await axios.post(
          `${API}/reported-change-stage`,
          {
            session_id: currentSessionId,
            language,
            reported_change_stage: canonical,
            traffic_source: trafficSource ? trafficSource.source : null,
            prolific_pid: trafficSource ? trafficSource.prolificPid : null,
            study_id: trafficSource ? trafficSource.studyId : null,
            prolific_session_id: trafficSource ? trafficSource.prolificSessionId : null,
          },
          { timeout: 4000 }
        );
      } catch {
        // research-only capture; silent failure MUST NOT block completion
      }
      setReportedChangeStageSubmitted(true);
      setTurns((prev) => [
        ...prev,
        { id: `breath-fb-${Date.now()}`, role: "breath" },
        { id: `fb-close1-${Date.now()}`, role: "ai", content: t.feedbackClosing1 },
        { id: `fb-close2-${Date.now()}`, role: "ai", content: t.feedbackClosing2, italic: true },
        { id: `feedback-${Date.now()}`, role: "feedback" },
      ]);
      // v6.11 Sprint 5C — passive process/effort metrics.
      // Fire-and-forget with an explicit timeout; failure MUST NOT block
      // the participant (research covariate, not a product signal).
      if (!metricsSentRef.current) {
        metricsSentRef.current = true;
        const now = Date.now();
        // Word-count rule (deterministic, documented):
        //   text.trim().split(/\s+/).filter(Boolean).length
        // applied across eligible reflection fields only:
        //   context (step 1), difficulty (step 2), tradeoff (step 3),
        //   values[i].reason free text (step 4), direction (step 5).
        // UI text, AI text, summary, clarity, Sprint 5A/B, feedback and
        // Prolific metadata are all excluded (per Sprint 5C rule 3).
        const countWords = (s) => {
          if (typeof s !== "string") return 0;
          const trimmed = s.trim();
          if (!trimmed) return 0;
          return trimmed.split(/\s+/).filter(Boolean).length;
        };
        const reflectionFields = [context, difficulty, tradeoff, direction];
        const reasonTexts = (selectedValues || []).map((v) => (v && v.reason) || "");
        const totalUserWordCount =
          reflectionFields.reduce((n, f) => n + countWords(f), 0) +
          reasonTexts.reduce((n, r) => n + countWords(r), 0);
        // Field count: 5 eligible reflection fields
        //   context, difficulty, tradeoff, values (as a whole), direction.
        // A field counts as completed when it holds a non-empty trimmed
        // response (or, for values, at least one selected value).
        const fieldCountCompleted =
          (context && context.trim() ? 1 : 0) +
          (difficulty && difficulty.trim() ? 1 : 0) +
          (tradeoff && tradeoff.trim() ? 1 : 0) +
          (selectedValues && selectedValues.length > 0 ? 1 : 0) +
          (direction && direction.trim() ? 1 : 0);
        // Timings — any missing anchor stores null (spec forbids invented
        // duration).
        const safeDelta = (aRef, bRef) => {
          const a = aRef.current;
          const b = bRef.current;
          if (a == null || b == null || b < a) return null;
          return b - a;
        };
        const timeInReflectionFlowMs = safeDelta(
          reflectionStartTsRef,
          preSummaryShownTsRef
        );
        const timeToSummaryMs = safeDelta(
          reflectionStartTsRef,
          summaryShownTsRef
        );
        const summaryViewTimeMs = safeDelta(
          summaryShownTsRef,
          clarityAfterProceedTsRef
        );
        const totalSessionTimeMs =
          sessionStartTsRef.current != null
            ? now - sessionStartTsRef.current
            : null;
        // Fire and forget — do not await; the participant flow has already
        // completed by this point and metrics latency must never surface.
        axios
          .post(
            `${API}/session-process-metrics`,
            {
              session_id: currentSessionId,
              language,
              total_user_word_count: totalUserWordCount,
              field_count_completed: fieldCountCompleted,
              time_in_reflection_flow_ms: timeInReflectionFlowMs,
              time_to_summary_ms: timeToSummaryMs,
              summary_view_time_ms: summaryViewTimeMs,
              total_session_time_ms: totalSessionTimeMs,
              traffic_source: trafficSource ? trafficSource.source : null,
              prolific_pid: trafficSource ? trafficSource.prolificPid : null,
              study_id: trafficSource ? trafficSource.studyId : null,
              prolific_session_id: trafficSource
                ? trafficSource.prolificSessionId
                : null,
            },
            { timeout: 4000 }
          )
          .catch(() => {
            /* research-only capture; silent failure MUST NOT block */
          });
      }
    },
    [currentSessionId, language, trafficSource, t, context, difficulty, tradeoff, selectedValues, direction]
  );

  const handleNewSession = () => {
    setTurns([]);
    setCurrentStep(0);
    setIsTyping(false);
    setDraft("");
    setContext("");
    setDifficulty("");
    setTradeoff("");
    setTradeoffClarified(false);
    setConversationFitStopped(false);
    setClarityBeforeSubmitted(false);
    setClarityAfterSubmitted(false);
    setSelectedValues([]);
    setValueReasons({});
    setCustomValue("");
    setDirection("");
    setSummary(null);
    // v6.3 Sprint 3B — reset signal-driven state so the input reappears.
    setSignalCompleted(false);
    setIsDetectingSignal(false);
    setMismatchCountByStep({});
    setSkippedSteps(new Set());
    submitGenerationRef.current = 0;
    actionTakenRef.current = false;
    bootDetectionFiredRef.current = false;
    // v6.7 Sprint 4B — reset Reflection Fit state.
    setReflectionFitCompleted(false);
    setReflectionFitAwaitingTransition(false);
    reflectionFitActionTakenRef.current = false;
    setPreSummarySubmitted(false);
    preSummaryTakenRef.current = false;
    setReportedChangeStageSubmitted(false);
    reportedChangeStageTakenRef.current = false;
    // Sprint 5C — reset passive timing refs on a fresh session.
    sessionStartTsRef.current = Date.now();
    reflectionStartTsRef.current = null;
    preSummaryShownTsRef.current = null;
    summaryShownTsRef.current = null;
    clarityAfterProceedTsRef.current = null;
    metricsSentRef.current = false;
    const id = `s_${Date.now()}`;
    bootSessionIdRef.current = id;
    setCurrentSessionId(id);

    // Replay boot sequence
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setTurns([
        { id: `boot-greeting-${id}`, role: "ai", content: t.greeting || "Welcome." },
      ]);
      setTimeout(() => {
        setTurns((prev) => [
          ...prev,
          { id: `boot-breath-${id}`, role: "breath" },
          // v6.3 Sprint 3B — restore the clarity-before gate so replayed
          // sessions still record clarity_before / clarity_change.
          { id: `clarity-before-${id}`, role: "clarity-before" },
        ]);
      }, BREATH_DELAY);
    }, TYPING_DELAY);
  };

  const handleSelectSession = () => {
    // simple: start new for now (we don't persist full content)
    handleNewSession();
  };

  /* ──── input affordance ──── */
  const convFitActive = turns.some((tt) => tt.role === "conv-fit");
  const signalBlockActive = turns.some((tt) => tt.role === "signal-block");
  const reflectionFitBlockActive = turns.some(
    (tt) => tt.role === "reflection-fit-block"
  );
  const preSummaryBlockActive = turns.some(
    (tt) => tt.role === "pre-summary-checkpoint"
  );
  const showInput =
    !convFitActive &&
    !signalBlockActive &&
    !reflectionFitBlockActive &&
    !preSummaryBlockActive &&
    !signalCompleted &&
    !reflectionFitCompleted &&
    ((currentStep >= 1 && currentStep <= 3) || currentStep === 5);
  const placeholder =
    currentStep === 1
      ? t.step1Placeholder
      : currentStep === 2
      ? t.step2Placeholder
      : currentStep === 3
      ? t.step3Placeholder
      : currentStep === 5
      ? t.step5Placeholder
      : "";

  /* ──── progress label ──── */
  const stepNumber =
    currentStep === 0
      ? 0
      : currentStep === 4
      ? 4
      : currentStep === 5
      ? 5
      : currentStep === 6
      ? TOTAL_STEPS
      : currentStep;
  const progressPct = Math.min(100, (stepNumber / TOTAL_STEPS) * 100);

  return (
    <div
      className="min-h-screen flex"
      style={{ background: "var(--bg)", color: "var(--ink)" }}
      data-testid="decision-flow"
    >
      <Sidebar
        t={t}
        sessions={sessions}
        currentId={currentSessionId}
        onNewSession={handleNewSession}
        onSelectSession={handleSelectSession}
      />

      {/* Main */}
      <div className="flex-1 flex flex-col" style={{ minHeight: "100vh" }}>
        {/* Top bar */}
        <header
          className="flex items-center justify-between px-5 md:px-10"
          style={{
            padding: "0.85rem 2.5rem",
            borderBottom: "1px solid var(--border-soft)",
          }}
        >
          <button
            onClick={() => navigate("/")}
            className="font-sans md:hidden flex items-center gap-2"
            style={{ color: "var(--ink-soft)", fontSize: "0.78rem" }}
            data-testid="back-home-btn"
          >
            ← {t.back}
          </button>
          <div
            className="hidden md:flex items-center gap-3"
            style={{
              // v6.5 Sprint 3D — progress indicator is hidden while a
              // terminal signal block is showing (or after completion) so
              // early-exit paths don't misleadingly display "STEP 5 OF 5".
              visibility:
                signalBlockActive ||
                signalCompleted ||
                reflectionFitBlockActive ||
                reflectionFitCompleted
                  ? "hidden"
                  : "visible",
            }}
            data-testid="progress-indicator-wrap"
          >
            <span
              className="font-sans"
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                color: "var(--ink-soft)",
                fontWeight: 500,
              }}
            >
              {t.stepOf
                .replace("{current}", String(Math.max(1, stepNumber)))
                .replace("{total}", String(TOTAL_STEPS))}
            </span>
            <div className="progress-line" data-testid="progress-bar">
              <div className="progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          <div className="md:hidden">
            <LanguageToggle />
          </div>
          <button
            onClick={handleNewSession}
            data-testid="header-new-session-btn"
            className="font-sans"
            style={{
              fontSize: "0.78rem",
              color: "var(--ink-soft)",
              letterSpacing: "0.04em",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontWeight: 400,
            }}
          >
            {t.newSession || "New session"}
          </button>
        </header>

        {/* Conversation thread */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto"
          style={{ padding: "2rem 1.25rem 1.5rem" }}
          data-testid="conversation-thread"
        >
          <div
            className="mx-auto"
            style={{ maxWidth: "640px", paddingBottom: showInput ? "9rem" : "3rem" }}
          >
            <div className="space-y-7">
              {turns.map((turn) => {
                if (turn.role === "ai") {
                  return (
                    <AiMessage
                      key={turn.id}
                      italic={turn.italic}
                      helper={turn.helper}
                      testId={`ai-message-${turn.id}`}
                    >
                      {turn.content}
                    </AiMessage>
                  );
                }
                if (turn.role === "user") {
                  return <UserMessage key={turn.id}>{turn.content}</UserMessage>;
                }
                if (turn.role === "breath") {
                  return <BreathTurn key={turn.id} />;
                }
                if (turn.role === "values") {
                  return (
                    <ValuesPicker
                      key={turn.id}
                      t={t}
                      selectedValues={selectedValues}
                      valueReasons={valueReasons}
                      onToggle={toggleValue}
                      onUpdateReason={updateValueReason}
                      customValue={customValue}
                      onCustomValueChange={setCustomValue}
                      onAddCustom={addCustomValue}
                      onContinue={handleValuesContinue}
                      onSkip={handleValuesSkip}
                      canContinue={selectedValues.length > 0}
                    />
                  );
                }
                if (turn.role === "summary") {
                  return (
                    <SummaryBlock
                      key={turn.id}
                      summary={turn.content}
                      t={t}
                      onStartOver={handleNewSession}
                      isSurveyCircle={isSurveyCircle}
                      surveyCode={surveyCode}
                      redeemUrl={redeemUrl}
                    />
                  );
                }
                if (turn.role === "feedback") {
                  return (
                    <FeedbackPanel
                      key={turn.id}
                      t={t}
                      sessionId={currentSessionId}
                      language={language}
                      trafficSource={trafficSource}
                    />
                  );
                }
                if (turn.role === "clarity-before") {
                  return (
                    <ClarityQuestion
                      key={turn.id}
                      t={t}
                      phase="before"
                      onSubmit={handleClarityBeforeDone}
                    />
                  );
                }
                if (turn.role === "clarity-after") {
                  return (
                    <ClarityQuestion
                      key={turn.id}
                      t={t}
                      phase="after"
                      onSubmit={handleClarityAfterDone}
                    />
                  );
                }
                if (turn.role === "conv-fit") {
                  return (
                    <ConversationFitBlock
                      key={turn.id}
                      t={t}
                      stopped={conversationFitStopped}
                      onTryAgain={handleNewSession}
                      onStop={async () => {
                        setConversationFitStopped(true);
                        try {
                          await axios.post(`${API}/session-event`, {
                            session_id: currentSessionId,
                            language,
                            event_type: "conversation_fit_stop",
                            triggering_step: currentStep,
                            traffic_source: trafficSource ? trafficSource.source : null,
                            prolific_pid: trafficSource ? trafficSource.prolificPid : null,
                          });
                        } catch (e) {
                          console.warn("session-event log failed", e);
                        }
                      }}
                    />
                  );
                }
                if (turn.role === "reported-change-stage-checkpoint") {
                  return (
                    <ReportedChangeStageCheckpoint
                      key={turn.id}
                      t={t}
                      submitted={reportedChangeStageSubmitted}
                      onSubmit={handleReportedChangeStageDone}
                    />
                  );
                }
                if (turn.role === "pre-summary-checkpoint") {
                  return (
                    <PreSummaryCheckpoint
                      key={turn.id}
                      t={t}
                      submitted={preSummarySubmitted}
                      onSubmit={async (canonical, freeText) => {
                        if (preSummaryTakenRef.current) return;
                        preSummaryTakenRef.current = true;
                        // Store the research answer (fire-and-forget: research
                        // capture failure MUST NOT block the participant from
                        // seeing their summary — that would harm the study).
                        // v6.9 Sprint 5A iter-32 hardening — explicit timeout
                        // so a slow/hanging /api/pre-summary-change cannot
                        // stall summary generation for the participant.
                        try {
                          await axios.post(
                            `${API}/pre-summary-change`,
                            {
                              session_id: currentSessionId,
                              language,
                              pre_summary_change: canonical,
                              pre_summary_change_text:
                                canonical === "a_little" || canonical === "yes"
                                  ? (freeText || "").trim() || null
                                  : null,
                              traffic_source: trafficSource ? trafficSource.source : null,
                              prolific_pid: trafficSource ? trafficSource.prolificPid : null,
                              study_id: trafficSource ? trafficSource.studyId : null,
                              prolific_session_id: trafficSource ? trafficSource.prolificSessionId : null,
                            },
                            { timeout: 4000 }
                          );
                        } catch {
                          // research-only capture; silent failure
                        }
                        setPreSummarySubmitted(true);
                        // Now generate the summary with the SAME payload as
                        // before Sprint 5A. Pre-summary values are NOT passed.
                        await generateSummary(turn._dir || "", turn._hes || "");
                      }}
                    />
                  );
                }
                if (turn.role === "reflection-fit-block") {
                  return (
                    <ReflectionFitBlock
                      key={turn.id}
                      t={t}
                      completed={reflectionFitCompleted}
                      onReflect={async () => {
                        if (reflectionFitActionTakenRef.current) return;
                        reflectionFitActionTakenRef.current = true;
                        // Research log: user chose to reflect personally.
                        try {
                          await axios.post(`${API}/session-event`, {
                            session_id: currentSessionId,
                            language,
                            event_type: "reflection_fit_action",
                            reflection_fit_action: "reflect_personally",
                            traffic_source: trafficSource ? trafficSource.source : null,
                            prolific_pid: trafficSource ? trafficSource.prolificPid : null,
                          });
                        } catch {
                          // research-only, silent
                        }
                        // Remove the fit-block from the transcript so the
                        // input reappears and the transition question can be
                        // asked in the normal conversational cadence.
                        setTurns((prev) => prev.filter((tt) => tt.id !== turn.id));
                        // Clear any pre-fill context — the ORIGINAL request
                        // must NOT be used as evidence for values/tension/
                        // trade-off/direction (Sprint 4B rule).
                        setContext("");
                        setReflectionFitAwaitingTransition(true);
                        setCurrentStep(1);
                        // v6.7 Sprint 4B — preserve Sprint 4A research parity:
                        // capture clarity_before for the boundary→reflect
                        // path just like the standard boot. The transition
                        // question is inserted AFTER the clarity gate by
                        // handleClarityBeforeDone (which reads the
                        // reflectionFitAwaitingTransition flag).
                        pushBreath();
                        setTurns((prev) => [
                          ...prev,
                          {
                            id: `clarity-before-fit-${Date.now()}`,
                            role: "clarity-before",
                          },
                        ]);
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                      onLeave={async () => {
                        if (reflectionFitActionTakenRef.current) return;
                        reflectionFitActionTakenRef.current = true;
                        setReflectionFitCompleted(true);
                        try {
                          await axios.post(`${API}/session-event`, {
                            session_id: currentSessionId,
                            language,
                            event_type: "reflection_fit_action",
                            reflection_fit_action: "leave",
                            traffic_source: trafficSource ? trafficSource.source : null,
                            prolific_pid: trafficSource ? trafficSource.prolificPid : null,
                          });
                        } catch {
                          // research-only, silent
                        }
                      }}
                    />
                  );
                }
                if (turn.role === "signal-block") {
                  const stepName =
                    turn.variant === "mismatch"
                      ? { 1: "context", 2: "difficulty", 3: "tradeoff", 5: "direction" }[
                          turn.step || currentStep
                        ] || "context"
                      : { 1: "context", 2: "difficulty", 3: "tradeoff", 4: "values", 5: "direction", 6: "summary" }[
                          currentStep
                        ] || "context";
                  return (
                    <SignalBlock
                      key={turn.id}
                      t={t}
                      variant={turn.variant}
                      hasGroundedMaterial={turn.hasGroundedMaterial}
                      completed={signalCompleted}
                      onMismatchSkip={async () => {
                        const step = turn.step || currentStep;
                        setTurns((prev) => prev.filter((tt) => tt.id !== turn.id));
                        await logSignalAction("mismatch", stepName, "skipped_question");
                        await advanceStepSkipping(step);
                      }}
                      onMismatchTryAgain={async () => {
                        setTurns((prev) => prev.filter((tt) => tt.id !== turn.id));
                        await logSignalAction("mismatch", stepName, "clarified_once");
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                      onStopFinish={async () => {
                        if (actionTakenRef.current) return;
                        actionTakenRef.current = true;
                        setSignalCompleted(true);
                        await logSignalAction("explicit_stop", stepName, "stopped");
                      }}
                      onSaturationFinish={async () => {
                        // v6.4 Sprint 3C — idempotent: guard against duplicate
                        // clarity-after inserts + duplicate action logs.
                        if (actionTakenRef.current) return;
                        actionTakenRef.current = true;
                        await logSignalAction("saturation", stepName, "shortened_summary");
                        setTurns((prev) => [
                          ...prev,
                          { id: `sat-clarity-after-${Date.now()}`, role: "clarity-after" },
                        ]);
                        setCurrentStep(6);
                      }}
                      onGnhStopHere={async () => {
                        if (actionTakenRef.current) return;
                        actionTakenRef.current = true;
                        setSignalCompleted(true);
                        await logSignalAction("global_not_helping", stepName, "stopped");
                      }}
                      onGnhStartAgain={async () => {
                        if (actionTakenRef.current) return;
                        actionTakenRef.current = true;
                        await logSignalAction("global_not_helping", stepName, "restart_offered");
                        navigate("/");
                      }}
                    />
                  );
                }
                return null;
              })}

              {isTyping && <TypingTurn />}
            </div>
          </div>
        </div>

        {/* Input bar */}
        {showInput && (
          <div
            className="sticky bottom-0"
            style={{
              padding: "1rem 1.25rem 1.4rem",
              background:
                "linear-gradient(to top, var(--bg) 60%, rgba(246,243,238,0))",
            }}
          >
            <div className="mx-auto" style={{ maxWidth: "640px" }}>
              <div
                className={`field-box ${isTyping || isLoading || isDetectingSignal || convFitActive || signalBlockActive || reflectionFitBlockActive ? "paused" : ""}`}
                data-testid="input-field"
              >
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={placeholder}
                  className="msg-field"
                  rows={1}
                  disabled={isTyping || isLoading || convFitActive}
                  data-testid="message-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!draft.trim() || isTyping || isLoading || convFitActive}
                  data-testid="send-btn"
                  aria-label="send"
                  className="absolute"
                  style={{
                    right: "0.55rem",
                    bottom: "0.55rem",
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: draft.trim() ? "var(--ink)" : "var(--line)",
                    color: draft.trim() ? "var(--bg)" : "var(--ink-ghost)",
                    cursor: draft.trim() ? "pointer" : "not-allowed",
                    border: "none",
                    transition: "background 0.2s",
                  }}
                >
                  {isLoading ? (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        border: "1.5px solid var(--bg)",
                        borderTopColor: "transparent",
                        animation: "spin 0.7s linear infinite",
                        display: "inline-block",
                      }}
                    />
                  ) : (
                    <ArrowUp className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p
                className="font-sans mt-2 text-center"
                style={{ fontSize: "0.72rem", color: "var(--ink-soft)", letterSpacing: "0.04em", fontWeight: 400 }}
              >
                {currentStep === 5
                  ? t.inputHintFinal || "Press Enter to receive your summary"
                  : t.inputHint || "Press Enter to continue · Shift + Enter for a new line"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionFlow;
